import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AutoGrowTextarea } from '@/components/common/AutoGrowTextarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Plus, Search, MoreHorizontal, Send, CheckCircle2, XCircle, Pencil, Trash2, ArrowRightCircle, FileDown, Link as LinkIcon, Tag, Eye, ScrollText, Users, FolderKanban, MapPin, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, Check, Building, Navigation, Loader2, Home, Landmark, HardHat, User, Phone, Mail, Map, AlertTriangle, Droplets, Wrench, Ruler, CircleDot, Settings2, Info, ExternalLink, GripVertical, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { SendDocumentDialog } from '@/components/common/SendDocumentDialog';
import AttachCustomForm from '@/components/forms/AttachCustomForm';
import AttachedFormsBadge, { useAttachedFormCounts } from '@/components/forms/AttachedFormsBadge';
import { ActivityLog } from '@/components/ActivityLog';
import DocumentsSection from '@/components/common/DocumentsSection';
import WellMapVerification from '@/components/common/WellMapVerification';
import WellLocationPicker from '@/components/common/WellLocationPicker';
import { Pagination } from '@/components/common/Pagination';
import { ContactCombobox } from '@/components/common/ContactCombobox';
import RelatedDocumentsBar from '@/components/common/RelatedDocumentsBar';
import { toTitleCase } from '@/lib/capitalize';
import { useCounties } from '@/hooks/useCounties';
import { contactPrimaryLabel } from '@/lib/contactDisplay';
import { US_STATES, stateNameToCode } from '@/constants/usStates';
import { useJobStatusConfig } from '@/hooks/useJobStatusConfig';
import { StatusStepper } from '@/components/shared/StatusStepper';
import 'leaflet/dist/leaflet.css';

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = digits.slice(0, 10);
  
  // Format based on length
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Blue Well Drilling Office Location (Sebring, FL)
const OFFICE_LOCATION = { lat: 27.4958, lng: -81.4411 };

// Florida Counties for property search
const FLORIDA_COUNTIES = null; // Replaced by useCounties hook

// Fix B (2026-08-21): Alian pidio el workflow completo del estimado en una barra
// arriba: "draft, sent, aceptado, reject, schedule, ready, complete". Los valores
// viejos NO se renombran ni se reordenan (hay estimados en produccion guardados
// con ellos); se AGREGAN schedule / ready / complete al final del flujo, despues
// de accepted.
const STATUSES = ['all', 'draft', 'sent', 'viewed', 'accepted', 'rejected', 'schedule', 'ready', 'complete', 'converted_to_invoice'];

// TEMA F3: best-effort city extraction from a comma-separated address string
// (e.g. an old lead conversion that stored the FULL "street, city, county,
// zip" text in well_address instead of the separate well_city field the
// wizard uses) — lets the UI self-heal old records instead of showing a
// false "missing city" warning.
const inferCityFromAddress = (address) => {
  if (!address) return '';
  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  return parts.length >= 2 ? parts[1] : '';
};

export default function EstimatesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  // Fix B (2026-08-21): Alian pidio poder dar acceso a los vendedores desde
  // Settings > Permissions ("ponelo acá abajo en los settings... para yo poder
  // darle acceso a la persona a cambiarlo o editarlo") — reemplaza el chequeo
  // hardcodeado por rol por el permiso real "estimates.change_status".
  // hasPermission() ya devuelve true para superadmin sin mirar la matriz, el
  // "|| user?.role === 'superadmin'" queda solo como respaldo explicito.
  const canAcceptEstimate = hasPermission('estimates', 'change_status') || user?.role === 'superadmin';
  const { config: jobStatusConfig } = useJobStatusConfig();
  const estimateStatusFlow = jobStatusConfig.estimate || [];
  const counties = useCounties();
  const [estimates, setEstimates] = useState([]);
  // Custom Forms attachment dialog (Alian video min 47:30 — "lo estoy haciendo desde el estimado")
  const [customFormsEstimate, setCustomFormsEstimate] = useState(null);
  const [customFormsOpen, setCustomFormsOpen] = useState(false);
  // Attached form counts per estimate (for the "X forms" badge)
  const formCounts = useAttachedFormCounts('estimate', estimates.map(e => e.id));
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(location.search).get('status') || 'all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [previewInlineOpen, setPreviewInlineOpen] = useState(false);
  const [previewInlineUrl, setPreviewInlineUrl] = useState(null);
  const [previewInlineLoading, setPreviewInlineLoading] = useState(false);
  const [sendDialogEst, setSendDialogEst] = useState(null);
  const [form, setForm] = useState({ 
    title: '', description: '', customer_id: '', project_id: '', notes: '', include_contract: true, 
    well_address: '', well_city: '', well_state: 'FL', well_zip: '', well_coordinates: { lat: null, lng: null }, 
    mailing_address: { address: '', city: '', state: 'FL', zip: '' },
    property_info: { parcel_id: '', owner_name: '', owner_address: '', owner_city: '', owner_state: 'FL', owner_zip: '', acreage: 0, use_description: '', just_value: 0, county: '' },
    client_type: 'owner', builder_name: '', builder_company: '', subcontractor_name: '', subcontractor_company: '',
    // Well Technical Information
    well_info: {
      well_use: 'domestic',
      contamination_zone: false,
      septic_distance: 75,
      construction_method: 'rotary',
      well_depth: 40,
      casing_depth: 0,
      casing_from: 0,
      casing_to: 0,
      casing_material: 'pvc',
      casing_diameter: '2',
      seal_material: 'sand',
      completion_intervals: [{ type: 'screen', from_depth: 80, to_depth: 100 }],
      casing_sections: [{ from_depth: 0, to_depth: 0, diameter: '2', material: 'pvc' }],
      work_type: 'new_construction',
      abandonment_reason: '',
      repair_reason: '',
      modification_reason: ''
    },
    discount_type: 'none', // none, percentage, fixed
    discount_value: 0,
    items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, sku: '', name: '' }] 
  });
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ 
    first_name: '', last_name: '', company_name: '', email: '', phone: '', 
    type: 'customer', account_type: 'residential', is_customer: true, address: '' 
  });
  const [newProjectForm, setNewProjectForm] = useState({ title: '', type: 'new_construction', address: '', city: '', county: '', state: 'FL', zip: '', lat: null, lng: null, parcel_id: '' });
  const [autoProjectNumber, setAutoProjectNumber] = useState('');
  const [loadingProjectNumber, setLoadingProjectNumber] = useState(false);
  
  // Templates state
  const [estimateTemplates, setEstimateTemplates] = useState([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Map and distance state
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [distance, setDistance] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  
  // Well Map Verification state
  const [showWellMapVerification, setShowWellMapVerification] = useState(false);
  const [projectCoordinates, setProjectCoordinates] = useState(null);
  const [addressVerified, setAddressVerified] = useState(false);
  const [wellVerificationData, setWellVerificationData] = useState(null);
  
  // Well Info Section state
  const [showWellInfoSection, setShowWellInfoSection] = useState(true);
  
  // Dropdown preferences (smart memory)
  const [dropdownPrefs, setDropdownPrefs] = useState({});
  
  // Accept dialog state
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [acceptEstimateId, setAcceptEstimateId] = useState(null);
  const [acceptComment, setAcceptComment] = useState('');

  // Reject dialog state (TEMA A4 — reject with a mandatory reason)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectEstimateId, setRejectEstimateId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Property search state
  const [searchingProperty, setSearchingProperty] = useState(false);
  const [propertyFound, setPropertyFound] = useState(null); // tri-state: null=not searched yet, true=found, false=searched, no record
  const [showManualPropertyEntry, setShowManualPropertyEntry] = useState(false);
  // Requirement F: "Mailing address is the same as job site address" — most
  // owners live where they build, so this saves re-typing the same address
  // twice. Unticked automatically the moment the county lookup finds a
  // DIFFERENT mailing address for the owner (e.g. KEYTECH Developments LLC
  // living in Fort Lauderdale while building in Sebring).
  const [mailingSameAsSite, setMailingSameAsSite] = useState(false);
  
  // Line item search
  const [lineItemSearch, setLineItemSearch] = useState('');
  const [lineItemResults, setLineItemResults] = useState([]);
  const [activeItemIdx, setActiveItemIdx] = useState(null);
  const searchTimeoutRef = useRef(null);
  
  // Counties covered by our parcel database (SWFWMD) — used to give a clear
  // message when a county has no parcel layer at all vs. genuinely no match
  // for that address (Alian's "4210 Navarre Avenue" report — Lee County isn't
  // in the SWFWMD district, so it will never return records here).
  const [supportedCounties, setSupportedCounties] = useState([]);
  useEffect(() => {
    api.get('/property/supported-counties').then(res => {
      setSupportedCounties(res.data?.counties || []);
    }).catch(() => {});
  }, []);
  const isCountySupported = useCallback((county) => {
    const norm = (county || '').trim().toLowerCase().replace(' county', '');
    return supportedCounties.includes(norm);
  }, [supportedCounties]);
  const countyLookupMessage = useCallback((county) => {
    if (county && !isCountySupported(county)) {
      const label = county.trim().replace(/\bcounty\b/i, '').trim();
      return `El condado de ${label} no está en nuestra base de datos de parcelas disponible. Ingrese el Parcel ID y el Property Owner manualmente.`;
    }
    return t('estimates.property.lookupEmpty');
  }, [isCountySupported, t]);

  // Requirement E: when the automatic owner/parcel lookup finds nothing for a
  // point, offer a direct link to that county's own Property Appraiser page
  // instead of leaving Alian stuck (matches what he does today via bookmarks).
  const [appraiserLink, setAppraiserLink] = useState(null);
  useEffect(() => {
    const county = newProjectForm.county || form.property_info?.county;
    if (!addressVerified || searchingProperty || propertyFound !== false || !county) {
      setAppraiserLink(null);
      return;
    }
    api.get('/property/appraiser-link', { params: { county } }).then(res => setAppraiserLink(res.data)).catch(() => setAppraiserLink(null));
  }, [addressVerified, searchingProperty, propertyFound, newProjectForm.county, form.property_info?.county]);

  // Initialize map when on step 3
  useEffect(() => {
    if (wizardStep === 3 && mapContainerRef.current && !mapInstanceRef.current) {
      const initMap = async () => {
        const L = await import('leaflet');
        
        // Clean up any existing map
        if (mapContainerRef.current._leaflet_id) {
          delete mapContainerRef.current._leaflet_id;
        }
        
        const map = L.map(mapContainerRef.current).setView([OFFICE_LOCATION.lat, OFFICE_LOCATION.lng], 9);
        mapInstanceRef.current = map;
        
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OSM'
        }).addTo(map);
        
        // Add office marker
        const officeIcon = L.divIcon({
          html: '<div style="background:#003366;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)"><svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z"/></svg></div>',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
          className: ''
        });
        L.marker([OFFICE_LOCATION.lat, OFFICE_LOCATION.lng], { icon: officeIcon })
          .addTo(map)
          .bindPopup('<strong>Blue Well Drilling</strong><br/>Oficina Principal');
        
        // If we already have coordinates, add the well marker
        if (form.well_coordinates?.lat && form.well_coordinates?.lng) {
          addWellMarker(map, L, form.well_coordinates.lat, form.well_coordinates.lng);
        }
      };
      initMap();
    }
    
    // Cleanup map when dialog closes or step changes
    return () => {
      if (mapInstanceRef.current && wizardStep !== 3) {
        try { mapInstanceRef.current.remove(); } catch {}
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [wizardStep, form.well_coordinates?.lat, form.well_coordinates?.lng]);

  // Clean up map when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch {}
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
      setDistance(null);
    }
  }, [dialogOpen]);

  // Add well marker to map
  const addWellMarker = async (map, L, lat, lng) => {
    // Remove existing well marker
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
    }
    
    const wellIcon = L.divIcon({
      html: '<div style="background:#3399FF;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg></div>',
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      className: ''
    });
    
    markerRef.current = L.marker([lat, lng], { icon: wellIcon })
      .addTo(map)
      .bindPopup('<strong>Ubicación del Pozo</strong>');
    
    // Fit bounds to show both markers
    const bounds = L.latLngBounds([
      [OFFICE_LOCATION.lat, OFFICE_LOCATION.lng],
      [lat, lng]
    ]);
    map.fitBounds(bounds, { padding: [30, 30] });
  };

  // Calculate distance between office and well location
  const calculateDistance = async (lat, lng) => {
    setCalculatingDistance(true);
    try {
      // Using OSRM for route distance
      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${OFFICE_LOCATION.lng},${OFFICE_LOCATION.lat};${lng},${lat}?overview=false`
      );
      const data = await response.json();
      
      if (data.routes && data.routes.length > 0) {
        const distanceKm = data.routes[0].distance / 1000;
        const distanceMiles = distanceKm * 0.621371;
        const durationMinutes = Math.round(data.routes[0].duration / 60);
        setDistance({ 
          km: distanceKm.toFixed(1), 
          miles: distanceMiles.toFixed(1),
          duration: durationMinutes
        });
      }
    } catch (err) {
      // Fallback to straight-line distance
      const R = 3959; // Earth's radius in miles
      const dLat = (lat - OFFICE_LOCATION.lat) * Math.PI / 180;
      const dLon = (lng - OFFICE_LOCATION.lng) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(OFFICE_LOCATION.lat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distanceMiles = R * c;
      setDistance({ 
        km: (distanceMiles / 0.621371).toFixed(1), 
        miles: distanceMiles.toFixed(1),
        duration: null,
        straight: true
      });
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = async (addressData) => {
    const lat = parseFloat(addressData.lat);
    const lng = parseFloat(addressData.lng);
    
    setForm(prev => ({
      ...prev,
      well_address: addressData.address,
      well_coordinates: { lat, lng }
    }));
    
    // Add marker to map
    if (mapInstanceRef.current) {
      const L = await import('leaflet');
      addWellMarker(mapInstanceRef.current, L, lat, lng);
    }
    
    // Calculate distance
    calculateDistance(lat, lng);
    
    // Search for property information based on coordinates
    searchPropertyByCoordinates(lat, lng, addressData.county, addressData.address);
  };

  // Search property information by coordinates
  const searchPropertyByCoordinates = async (lat, lng, county = '', address = '') => {
    setSearchingProperty(true);
    setPropertyFound(null);
    try {
      const response = await api.get('/property/by-coordinates', { 
        params: { lat, lng, county: county || '', address: address || '' } 
      });
      if (response.data && response.data.parcel_id) {
        setForm(prev => ({
          ...prev,
          property_info: {
            parcel_id: response.data.parcel_id || '',
            owner_name: response.data.owner_name || '',
            owner_address: response.data.owner_address || '',
            owner_city: response.data.owner_city || '',
            owner_state: response.data.owner_state || 'FL',
            owner_zip: response.data.owner_zip || '',
            acreage: response.data.acreage || 0,
            use_description: response.data.use_description || '',
            just_value: response.data.just_value || 0,
            county: response.data.property_county || county
          }
        }));
        setPropertyFound(true);
        toast.success(t('estimates.property.foundProperty'));
      } else {
        // No parcel at this location — warn instead of leaving fields silently blank.
        // Distinguish "county not in our parcel database" from "no match found".
        setPropertyFound(false);
        toast.warning(countyLookupMessage(county), { duration: 7000 });
      }
    } catch (err) {
      console.log('Property lookup failed:', err);
      setPropertyFound(false);
      toast.warning(countyLookupMessage(county), { duration: 7000 });
    } finally {
      setSearchingProperty(false);
    }
  };

  // Search property by parcel ID
  const searchPropertyByParcelId = async (parcelId) => {
    if (!parcelId || parcelId.length < 3) return;
    const county = form.property_info?.county || newProjectForm.county || '';
    setSearchingProperty(true);
    setPropertyFound(null);
    try {
      const response = await api.get('/property/search', { 
        params: { parcel_id: parcelId, county } 
      });
      if (response.data && response.data.length > 0) {
        const prop = response.data[0];
        setForm(prev => ({
          ...prev,
          property_info: {
            parcel_id: prop.parcel_id || '',
            owner_name: prop.owner_name || '',
            owner_address: prop.owner_address || '',
            owner_city: prop.owner_city || '',
            owner_state: prop.owner_state || 'FL',
            owner_zip: prop.owner_zip || '',
            acreage: prop.acreage || 0,
            use_description: prop.use_description || '',
            just_value: prop.just_value || 0,
            county: prop.property_county || ''
          }
        }));
        setPropertyFound(true);
        toast.success(t('estimates.property.foundProperty'));
      } else {
        setPropertyFound(false);
      }
    } catch (err) {
      setPropertyFound(false);
      toast.error(t('estimates.property.notFound'));
    } finally {
      setSearchingProperty(false);
    }
  };

  // TEMA F2: retry the FDOR parcel search from the Job Site Address already
  // on the estimate — for leads converted before a parcel could be found
  // (address typo, county temporarily down, etc.) instead of leaving the
  // Parcel ID/owner fields silently blank forever.
  const retrySearchParcel = async () => {
    if (!form.well_address) return;
    setSearchingProperty(true);
    setPropertyFound(null);
    try {
      const response = await api.get('/property/search', {
        params: { address: form.well_address, county: form.property_info?.county || '' }
      });
      if (response.data && response.data.length > 0) {
        const prop = response.data[0];
        setForm(prev => ({
          ...prev,
          property_info: {
            parcel_id: prop.parcel_id || '', owner_name: prop.owner_name || '',
            owner_address: prop.owner_address || '', owner_city: prop.owner_city || '',
            owner_state: prop.owner_state || 'FL', owner_zip: prop.owner_zip || '',
            acreage: prop.acreage || 0, use_description: prop.use_description || '',
            just_value: prop.just_value || 0, county: prop.property_county || prev.property_info?.county || ''
          }
        }));
        setPropertyFound(true);
        toast.success(t('estimates.property.foundProperty'));
      } else {
        setPropertyFound(false);
        toast.warning(t('estimates.property.notFound'));
      }
    } catch {
      setPropertyFound(false);
      toast.error(t('estimates.property.notFound'));
    } finally {
      setSearchingProperty(false);
    }
  };


  const fetchData = useCallback(async () => {
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const [estRes, contRes, projRes] = await Promise.all([
        api.get('/estimates', { params }),
        api.get('/contacts', { params: { page_size: 500 } }),
        api.get('/projects')
      ]);
      const estData = estRes.data;
      setEstimates(estData.items || []);
      setTotal(estData.total || 0);
      setTotalPages(estData.total_pages || 1);
      const contData = contRes.data;
      setContacts(contData.items || contData);
      setProjects(projRes.data);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [statusFilter, search, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch dropdown preferences (smart memory)
  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await api.get(`${API_URL}/api/dropdown-preferences`);
        // Normalize values to lowercase to match dropdown option values
        const raw = res.data || {};
        const normalized = {};
        const valueAliases = { construction: 'new_construction' };
        for (const [key, values] of Object.entries(raw)) {
          normalized[key] = (values || []).map(v => {
            const val = (v.value || '').toLowerCase().replace(/ /g, '_');
            return { ...v, value: valueAliases[val] || val };
          });
        }
        setDropdownPrefs(normalized);
      } catch { /* silent - preferences are optional */ }
    };
    fetchPrefs();
  }, []);

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  // Fetch estimate templates
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await api.get('/estimate-templates');
      setEstimateTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Apply template to current estimate
  const applyTemplate = (template) => {
    if (!template || !template.items || template.items.length === 0) {
      toast.error(t('estimates.templateEmpty'));
      return;
    }
    
    // Map template items to form items
    const newItems = template.items.map(item => ({
      sku: item.sku || '',
      name: item.name || item.description || '',
      description: item.description || '',
      quantity: item.quantity || 1,
      unit: item.unit || 'unit',
      unit_price: item.unit_price || 0
    }));
    
    setForm(prev => ({
      ...prev,
      items: newItems,
      description: template.description || prev.description,
      notes: template.notes || prev.notes
    }));
    
    setShowTemplateSelector(false);
    toast.success(t('estimates.templateApplied', { name: template.name, count: newItems.length }));
    
    // Register template usage
    api.post(`/estimate-templates/${template.id}/use`).catch(() => {});
  };

  // Handle createFor from Contacts page navigation
  useEffect(() => {
    if (location.state?.createFor && contacts.length > 0) {
      const customerId = location.state.createFor;
      const contactName = location.state.contactName || '';
      const date = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      setEditing(null);
      setForm({ 
        title: contactName ? `${contactName} - ${date}` : '', 
        description: '', 
        customer_id: customerId, 
        project_id: '', 
        notes: '', 
        include_contract: true,
        well_address: '', well_city: '', well_state: 'FL', well_zip: '', well_coordinates: { lat: null, lng: null }, 
        property_info: { parcel_id: '', owner_name: '', owner_address: '', owner_city: '', owner_state: 'FL', owner_zip: '', acreage: 0, use_description: '', just_value: 0, county: '' },
        client_type: 'owner', builder_name: '', builder_company: '', subcontractor_name: '', subcontractor_company: '',
        discount_type: 'none', discount_value: 0,
        items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, sku: '', name: '' }] 
      });
      setLineItemSearch('');
      setLineItemResults([]);
      setActiveItemIdx(null);
      setWizardStep(1);
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
      toast.info(`Creando estimado para ${contactName}`);
    }
  }, [location.state, contacts, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle useTemplate from Templates page navigation
  useEffect(() => {
    if (location.state?.useTemplate) {
      const template = location.state.useTemplate;
      setEditing(null);
      setForm({ 
        title: template.name || '', 
        description: template.description || '', 
        customer_id: '', 
        project_id: '', 
        notes: template.notes || '', 
        include_contract: true,
        discount_type: 'none', discount_value: 0,
        items: template.items?.length ? template.items.map(i => ({
          sku: i.sku || '',
          name: i.name || '',
          description: i.description || '',
          quantity: i.quantity || 1,
          unit: i.unit || 'unit',
          unit_price: i.unit_price || 0
        })) : [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, sku: '', name: '' }]
      });
      setLineItemSearch('');
      setLineItemResults([]);
      setActiveItemIdx(null);
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
      toast.success(`Plantilla "${template.name}" cargada - Solo falta seleccionar el cliente`);
      
      // Register template usage
      api.post(`/estimate-templates/${template.id}/use`).catch(() => {});
    }
  }, [location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle openEstimateId from Project Detail / Contact Profile navigation.
  // Fetch the estimate DIRECTLY by id instead of looking it up in the
  // paginated `estimates` list — with 25/page, an older estimate that isn't
  // on the current page never matched `.find()`, silently landing the user
  // on the plain list instead of the document they clicked (TEMA B1).
  useEffect(() => {
    if (location.state?.openEstimateId) {
      const estimateId = location.state.openEstimateId;
      navigate(location.pathname, { replace: true, state: {} });
      api.get(`/estimates/${estimateId}`).then(res => {
        openEdit(res.data);
      }).catch(() => {
        toast.error('No se pudo abrir el estimado');
      });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle createForProject from Project Detail navigation.
  // Open the create wizard already tied to the project: prefill customer + job
  // site + property from the project and jump past the customer/project steps.
  useEffect(() => {
    if (location.state?.createForProject) {
      const projectId = location.state.createForProject;
      const customerId = location.state.createFor || '';
      openCreate();
      setTimeout(() => {
        if (customerId) handleCustomerSelect(customerId);
        handleProjectSelect(projectId);
        setWizardStep(3);
      }, 120);
      navigate(location.pathname, { replace: true, state: {} });
      toast.info(t('estimates.creatingForProject', { defaultValue: 'Creating estimate for this project' }));
    }
  }, [location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open wizard when navigated from Maps with pre-filled address
  useEffect(() => {
    if (location.state?.fromMap) {
      openCreate();
      const { wellAddress, wellCoordinates, city, zip, state: stateVal, county, ownerName, parcelId } = location.state;
      setTimeout(() => {
        setForm(prev => ({
          ...prev,
          well_address: wellAddress || prev.well_address,
          well_coordinates: wellCoordinates || prev.well_coordinates,
          property_info: {
            ...prev.property_info,
            county: county || prev.property_info?.county || '',
            owner_name: ownerName || prev.property_info?.owner_name || '',
            parcel_id: parcelId || prev.property_info?.parcel_id || '',
          },
        }));
        // Also pre-fill newProjectForm so Step 2 (Create New Project) doesn't
        // require re-typing the address Alian already searched on the map —
        // includes City/Zip now too (Alian: "no me sale la ciudad ni el zip
        // code, no se por que no esta populando eso; dice Sarasota nada mas").
        setNewProjectForm(prev => ({
          ...prev,
          address: (wellAddress || '').split(',')[0] || prev.address,
          city: city || prev.city,
          zip: zip || prev.zip,
          state: stateVal || prev.state,
          county: county || prev.county,
          lat: wellCoordinates?.lat || prev.lat,
          lng: wellCoordinates?.lng || prev.lng,
          parcel_id: parcelId || prev.parcel_id,
          title: ownerName && !prev.title ? ownerName : prev.title,
        }));
        // NOTE: We intentionally do NOT pre-fill/open the "new customer" form
        // with the property owner's name here. The property owner (from
        // county records) is almost never the customer requesting the
        // estimate (Alian's feedback: "Armando Rodriguez no es la persona
        // que me esta pidiendo"). The owner stays as property_info.owner_name
        // (Job Address/Parcel data) — the customer is chosen explicitly by
        // Alian on Step 1 (search existing contact or create a new one).
      }, 150);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setIsViewMode(false);
    setForm({ 
      title: '', description: '', customer_id: '', project_id: '', notes: '', include_contract: true, 
      well_address: '', well_city: '', well_state: 'FL', well_zip: '', well_coordinates: { lat: null, lng: null }, 
      property_info: { parcel_id: '', owner_name: '', owner_address: '', owner_city: '', owner_state: 'FL', owner_zip: '', acreage: 0, use_description: '', just_value: 0, county: '' },
      client_type: 'owner', builder_name: '', builder_company: '', subcontractor_name: '', subcontractor_company: '',
      well_info: {
        well_use: 'domestic', contamination_zone: false, septic_distance: 75,
        construction_method: 'rotary', well_depth: 40, casing_depth: 0, casing_from: 0, casing_to: 0, casing_material: 'pvc',
        casing_diameter: '2', seal_material: 'sand', completion_intervals: [{ type: 'screen', from_depth: 80, to_depth: 100 }],
        casing_sections: [{ from_depth: 0, to_depth: 0, diameter: '2', material: 'pvc' }],
        work_type: 'new_construction', abandonment_reason: '', repair_reason: '', modification_reason: ''
      },
      discount_type: 'none', discount_value: 0,
      items: [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, sku: '', name: '' }] 
    });
    setLineItemSearch('');
    setLineItemResults([]);
    setActiveItemIdx(null);
    setWizardStep(1);
    setShowNewCustomerForm(false);
    setShowNewProjectForm(false);
    setNewCustomerForm({ first_name: '', last_name: '', company_name: '', email: '', phone: '', type: 'customer', account_type: 'residential', is_customer: true, address: '' });
    setNewProjectForm({ title: '', type: 'new_construction', address: '', city: '', county: '', state: 'FL', zip: '', lat: null, lng: null });
    setDistance(null);
    setPropertyFound(null);
    setShowManualPropertyEntry(false);
    setAddressVerified(false);
    setWellVerificationData(null);
    setDialogOpen(true);
  };

  const openEdit = (est) => {
    setEditing(est);
    setIsViewMode(true);
    const defaultWellInfo = {
      well_use: 'domestic', contamination_zone: false, septic_distance: 75,
      construction_method: 'rotary', well_depth: 40, casing_depth: 0, casing_from: 0, casing_to: 0, casing_material: 'pvc',
      casing_diameter: '2', seal_material: 'sand', completion_intervals: [{ type: 'screen', from_depth: 80, to_depth: 100 }],
      casing_sections: [{ from_depth: 0, to_depth: 0, diameter: '2', material: 'pvc' }],
      work_type: 'new_construction', abandonment_reason: '', repair_reason: '', modification_reason: ''
    };
    setForm({
      title: est.title, description: est.description || '', customer_id: est.customer_id || '',
      project_id: est.project_id || '', notes: est.notes || '', include_contract: est.include_contract !== false,
      well_address: est.well_address || '',
      well_city: est.well_city || inferCityFromAddress(est.well_address),
      well_state: est.well_state || 'FL',
      well_zip: est.well_zip || '',
      well_coordinates: est.well_coordinates || { lat: null, lng: null },
      mailing_address: est.mailing_address || { address: '', city: '', state: 'FL', zip: '' },
      property_info: est.property_info || { parcel_id: '', owner_name: '', owner_address: '', owner_city: '', owner_state: 'FL', owner_zip: '', acreage: 0, use_description: '', just_value: 0, county: '' },
      client_type: est.client_type || 'owner',
      builder_name: est.builder_name || '',
      builder_company: est.builder_company || '',
      subcontractor_name: est.subcontractor_name || '',
      subcontractor_company: est.subcontractor_company || '',
      well_info: (() => {
        const merged = { ...defaultWellInfo, ...(est.well_info || {}) };
        // Backward compatibility: convert old screen_type/screen_from/screen_to to completion_intervals
        // Check original est.well_info (not merged) to avoid defaultWellInfo masking old format
        const origWellInfo = est.well_info || {};
        if (!origWellInfo.completion_intervals || !Array.isArray(origWellInfo.completion_intervals) || origWellInfo.completion_intervals.length === 0) {
          const oldType = origWellInfo.screen_type || merged.screen_type || 'screen';
          const oldFrom = origWellInfo.screen_from || merged.screen_from || 80;
          const oldTo = origWellInfo.screen_to || merged.screen_to || 100;
          merged.completion_intervals = [{ type: oldType === 'open_hole' ? 'open_hole' : 'screen', from_depth: oldFrom, to_depth: oldTo }];
        }
        // Backward compatibility: convert old single casing_depth to casing_from/casing_to range
        // Must check origWellInfo (not merged) because defaultWellInfo already sets casing_from/casing_to to 0
        if ((origWellInfo.casing_from === undefined || origWellInfo.casing_from === null) &&
            (origWellInfo.casing_to === undefined || origWellInfo.casing_to === null)) {
          merged.casing_from = 0;
          merged.casing_to = origWellInfo.casing_depth || merged.casing_depth || 0;
        }
        // Backward compatibility: convert old casing fields to casing_sections array
        if (!origWellInfo.casing_sections || !Array.isArray(origWellInfo.casing_sections) || origWellInfo.casing_sections.length === 0) {
          merged.casing_sections = [{
            from_depth: merged.casing_from || 0,
            to_depth: merged.casing_to || merged.casing_depth || 0,
            diameter: merged.casing_diameter || '2',
            material: merged.casing_material || merged.casing_type || 'pvc'
          }];
        }
        return merged;
      })(),
      discount_type: est.discount_type || 'none',
      discount_value: est.discount_value || 0,
      items: est.items?.length ? est.items.map(i => ({ 
        description: i.description || '', 
        quantity: i.quantity || 1, 
        unit: i.unit || 'unit', 
        unit_price: i.unit_price || 0,
        sku: i.sku || '',
        name: i.name || ''
      })) : [{ description: '', quantity: 1, unit: 'unit', unit_price: 0, sku: '', name: '' }]
    });
    setLineItemSearch('');
    setLineItemResults([]);
    setActiveItemIdx(null);
    setWizardStep(4); // For editing, go directly to items
    setDistance(null);
    setDialogOpen(true);
  };

  // Create new customer inline
  const handleCreateCustomer = async () => {
    if (!newCustomerForm.first_name.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    try {
      const res = await api.post('/contacts', newCustomerForm);
      setContacts([...contacts, res.data]);
      // Auto-generate title with new customer
      const customerName = `${res.data.first_name} ${res.data.last_name}`.trim();
      const date = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const autoTitle = !editing && !form.title ? `${customerName} - ${date}` : form.title;
      setForm({ ...form, customer_id: res.data.id, title: autoTitle });
      setShowNewCustomerForm(false);
      setNewCustomerForm({ first_name: '', last_name: '', company_name: '', email: '', phone: '', type: 'customer', account_type: 'residential', is_customer: true, address: '' });
      toast.success(t('estimates.wizard.newCustomerCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  // Fetch next project number automatically
  // NOTE: We intentionally do NOT copy the project number into the title field —
  // the title should describe the project (auto-filled from the job site address)
  // and remain distinct from the project number identifier.
  const fetchNextProjectNumber = async () => {
    setLoadingProjectNumber(true);
    try {
      const res = await api.get('/projects/next-number');
      setAutoProjectNumber(res.data.project_number);
    } catch (err) {
      console.error('Error fetching project number:', err);
      // Fallback: generate locally
      const year = new Date().getFullYear();
      const fallbackNumber = `PRJ-${year}-${String(projects.length + 1).padStart(3, '0')}`;
      setAutoProjectNumber(fallbackNumber);
    } finally {
      setLoadingProjectNumber(false);
    }
  };

  // Handle opening new project form with auto-generated number
  const handleOpenNewProjectForm = async () => {
    setShowNewProjectForm(true);
    await fetchNextProjectNumber();
  };

  // Create new project inline
  const handleCreateProject = async () => {
    // Title (Property Owner) is auto-filled from county lookup. Fallback to address+city
    // if owner wasn't found and user didn't manually enter a name.
    let projectTitle = newProjectForm.title.trim();
    if (!projectTitle) {
      const addr = (newProjectForm.address || '').trim();
      const city = (newProjectForm.city || '').trim();
      projectTitle = [toTitleCase(addr), toTitleCase(city)].filter(Boolean).join(', ');
    }
    // Fix E (2026-08-31): vacant-lot projects have neither an owner name nor
    // a street address — only a parcel number. Without this, handleCreateProject
    // silently refused to create the project ("Este campo es obligatorio"),
    // which was the REAL final blocker even after the Verify button and
    // parcel lookup were fixed. The parcel number becomes the identifying
    // title, same spot an address would normally show.
    if (!projectTitle && newProjectForm.parcel_id) {
      projectTitle = `Parcel ${newProjectForm.parcel_id.trim()}`;
    }
    if (!projectTitle) {
      toast.error(t('errors.requiredField'));
      return null;
    }
    try {
      const projectData = {
        title: projectTitle,
        type: newProjectForm.type,
        customer_id: form.customer_id,
        location: {
          address: newProjectForm.address || '',
          city: newProjectForm.city || '',
          county: newProjectForm.county || '',
          state: newProjectForm.state || 'FL',
          zip: newProjectForm.zip || '',
          lat: newProjectForm.lat || null,
          lng: newProjectForm.lng || null,
          parcel_id: newProjectForm.parcel_id || '',
        }
      };
      const res = await api.post('/projects', projectData);
      setProjects(prev => [...prev, res.data]);
      // Update title with project info if customer is selected
      const newTitle = !editing && form.customer_id ? generateEstimateTitle(form.customer_id, res.data.id) : form.title;
      // Auto-populate well_address and property_info from the new project
      setForm(prev => ({
        ...prev,
        project_id: res.data.id,
        title: newTitle,
        well_address: newProjectForm.address || prev.well_address,
        well_city: newProjectForm.city || prev.well_city,
        well_state: newProjectForm.state || prev.well_state || 'FL',
        well_zip: newProjectForm.zip || prev.well_zip,
        well_coordinates: (newProjectForm.lat && newProjectForm.lng) ? { lat: newProjectForm.lat, lng: newProjectForm.lng } : prev.well_coordinates,
        property_info: {
          ...prev.property_info,
          parcel_id: newProjectForm.parcel_id || prev.property_info?.parcel_id || '',
          county: newProjectForm.county || prev.property_info?.county || '',
        }
      }));
      setShowNewProjectForm(false);
      setNewProjectForm({ title: '', type: 'new_construction', address: '', city: '', county: '', state: 'FL', zip: '', lat: null, lng: null, parcel_id: '' });
      setAutoProjectNumber('');
      toast.success(t('estimates.wizard.newProjectCreated') + ` (${res.data.project_number})`);
      return res.data; // Return created project for auto-advance flow
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
      return null;
    }
  };

  // Verify address on SWFWMD well map
  const handleVerifyAddress = async () => {
    const { address, city, county, state, zip, lat, lng, parcel_id } = newProjectForm;

    // Fix E (2026-08-31): terrenos baldios sin direccion fisica, solo numero de
    // parcela ("lo unico que me puede dar la persona es el numero de parcela...
    // y no me estas dejando salir hacia adelante"). Camino nuevo: parcel_id ->
    // boundary de la parcela -> mismo pipeline de confirmacion que ya usa el
    // camino por direccion (WellMapVerification + handleVerificationConfirmed),
    // sin tocar ni una linea de esa logica.
    if (!address && parcel_id) {
      await handleVerifyByParcel(parcel_id, county);
      return;
    }

    if (!address || address.length < 3) {
      toast.error('Por favor ingresa una dirección válida');
      return;
    }
    
    // Strategy 1: Use coordinates already captured from autocomplete selection
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      setProjectCoordinates({ lat, lng });
      setShowWellMapVerification(true);
      return;
    }
    
    // Strategy 2: Use backend geocoding endpoint with multiple fallback strategies
    try {
      const params = new URLSearchParams();
      params.append('address', address);
      if (city) params.append('city', city);
      if (county) params.append('county', county);
      params.append('state', state || 'Florida');
      if (zip) params.append('zip_code', zip);
      
      const res = await api.get(`/geocode/address?${params.toString()}`);
      
      if (res.data?.success && res.data.lat && res.data.lng) {
        const coords = { lat: res.data.lat, lng: res.data.lng };
        setProjectCoordinates(coords);
        const details = res.data.address_details || {};
        // Backfill city/county/zip too (not just lat/lng) — this is the
        // fallback path taken when the user typed+confirmed the address
        // without picking a dropdown option, so nothing else populates
        // these fields. Without this, handleNextStep()'s "County is
        // required" gate blocks the wizard even though the address WAS
        // successfully verified (Alian's exact "confirmar sin tocar el
        // desplegable" flow).
        setNewProjectForm(prev => ({
          ...prev,
          lat: coords.lat,
          lng: coords.lng,
          city: details.city || prev.city,
          county: details.county || prev.county,
          state: stateNameToCode(details.state) || prev.state || 'FL',
          zip: details.postcode || prev.zip,
        }));
        setShowWellMapVerification(true);
        if (res.data.accuracy === 'low') {
          toast.info('Ubicación aproximada basada en código postal/ciudad.');
        }
        return;
      }
    } catch (err) {
      console.error('Backend geocoding error:', err);
    }
    
    // Last resort: Use Florida center coordinates with clear warning
    toast.warning('No se pudo geocodificar la dirección exacta. Mostrando ubicación del área central de Florida.');
    const fallbackCoords = { lat: 27.4958, lng: -81.4411 };
    setProjectCoordinates(fallbackCoords);
    // Persist fallback coords too (Strategies 1 & 2 already do this) so the
    // project always has some lat/lng and the Well Location map can render
    // later on the Estimate Items step instead of silently disappearing.
    setNewProjectForm(prev => ({ ...prev, lat: fallbackCoords.lat, lng: fallbackCoords.lng }));
    setShowWellMapVerification(true);
  };

  // Fix E (2026-08-31): Alian's "solo tengo el número de parcela" case — a
  // vacant lot the customer is considering buying, with no street address
  // assigned yet, only a parcel/PID. Uses the parcel boundary geometry to
  // find a point INSIDE the parcel (its centroid) and hands off to the exact
  // same confirmation pipeline the address flow already uses, so the final
  // state (addressVerified, owner lookup, map) is identical either way.
  const handleVerifyByParcel = async (parcelId, county) => {
    try {
      const params = { parcel_id: parcelId };
      if (county) params.county = county;
      const boundaryRes = await api.get('/property/boundary', { params });
      const rings = boundaryRes.data?.boundary || [];
      if (rings.length > 0 && rings[0].length > 0) {
        const ring = rings[0];
        const coords = {
          lat: ring.reduce((s, p) => s + p[0], 0) / ring.length,
          lng: ring.reduce((s, p) => s + p[1], 0) / ring.length,
        };
        setNewProjectForm(prev => ({ ...prev, lat: coords.lat, lng: coords.lng }));
        setProjectCoordinates(coords);
        setShowWellMapVerification(true);
        return;
      }
      // No boundary geometry, but the parcel record itself might still exist
      // (owner/attributes without a drawable polygon) — never leave Alian
      // stuck, populate whatever we have and mark it verified.
      const searchRes = await api.get('/property/search', { params });
      const prop = Array.isArray(searchRes.data) ? searchRes.data[0] : null;
      if (!prop || (!prop.owner_name && !prop.parcel_id)) {
        toast.error('Parcel not found. Check the number or select the county.');
        return;
      }
      setNewProjectForm(prev => ({
        ...prev,
        county: prop.property_county || prev.county,
        title: prev.title || prop.owner_name || prev.title,
      }));
      setForm(prev => ({
        ...prev,
        property_info: {
          ...prev.property_info,
          parcel_id: parcelId,
          owner_name: prop.owner_name || prev.property_info?.owner_name || '',
          owner_address: prop.owner_address || prev.property_info?.owner_address || '',
          owner_city: prop.owner_city || prev.property_info?.owner_city || '',
          owner_state: prop.owner_state || prev.property_info?.owner_state || 'FL',
          owner_zip: prop.owner_zip || prev.property_info?.owner_zip || '',
          county: prop.property_county || prev.property_info?.county || '',
        },
      }));
      setAddressVerified(true);
      toast.success(t('estimates.addressVerified'));
    } catch (err) {
      toast.error('Parcel not found. Check the number or select the county.');
    }
  };

  // Handle verification confirmation
  const handleVerificationConfirmed = async (verificationData) => {
    setAddressVerified(true);
    setWellVerificationData(verificationData);
    setShowWellMapVerification(false);
    toast.success(t('estimates.addressVerified'));

    // Alian's video request (min 7:00–7:30): "When I type the address, the system
    // should automatically pull the legal homeowner's name and parcel ID from
    // county records." After the well map is confirmed, we have lat/lng — use
    // them to auto-fetch the property record and populate property_info AND
    // newProjectForm so it survives into the created Project.
    const coords = projectCoordinates || verificationData?.coordinates || null;
    const county = newProjectForm.county || verificationData?.county;
    if (coords?.lat && coords?.lng) {
      try {
        setSearchingProperty(true);
        setPropertyFound(null);
        const res = await api.get('/property/by-coordinates', { params: { lat: coords.lat, lng: coords.lng, county: county || '', address: newProjectForm.address || '' } });
        const prop = res.data;
        if (prop && (prop.parcel_id || prop.owner_name)) {
          // Hydrate property_info on the estimate form
          setForm(prev => ({
            ...prev,
            property_info: {
              ...prev.property_info,
              parcel_id: prop.parcel_id || prev.property_info?.parcel_id || '',
              owner_name: prop.owner_name || prev.property_info?.owner_name || '',
              owner_address: prop.owner_address || prev.property_info?.owner_address || '',
              owner_city: prop.owner_city || prev.property_info?.owner_city || '',
              owner_state: prop.owner_state || prev.property_info?.owner_state || 'FL',
              owner_zip: prop.owner_zip || prev.property_info?.owner_zip || '',
              acreage: prop.acreage || prev.property_info?.acreage || 0,
              use_description: prop.use_description || prev.property_info?.use_description || '',
              just_value: prop.just_value || prev.property_info?.just_value || 0,
              county: prop.property_county || county || prev.property_info?.county || '',
            },
          }));
          // Owner has a distinct mailing address on record — uncheck "same as
          // job site" so the found address is shown/used instead of the site.
          if (prop.owner_address) setMailingSameAsSite(false);
          // Mirror parcel_id back into the new-project form so it persists with the project.
          // Also auto-fill the project title with the owner name (Alian's video request 1:09-2:19:
          // the "Project Name" field should be labeled "Property Owner" and contain the legal owner name).
          // Fallback 1.4: if the geocoder never resolved a city (Alian's "4101
          // Indiana Drive" case — Charlotte County resolved fine, city didn't),
          // pull it from the FDOR parcel record instead of giving up.
          setNewProjectForm(prev => ({
            ...prev,
            parcel_id: prop.parcel_id || prev.parcel_id || '',
            city: prev.city || (prop.property_city ? toTitleCase(prop.property_city) : prev.city),
            title: prop.owner_name && !prev.title ? prop.owner_name : prev.title,
            // Fix E (2026-08-31): the parcel-only path (no street address) never
            // runs the geocoder/autocomplete step that normally fills county on
            // newProjectForm, so without this mirror the county-required check
            // in handleNextStep would block the wizard even though verification
            // succeeded. No-op for the address flow (prev.county already set).
            county: prev.county || prop.property_county || county || '',
          }));
          setPropertyFound(true);
          if (prop.owner_name) {
            toast.success(`Dueño legal encontrado: ${prop.owner_name}`);
          }
        } else {
          // No parcel record — warn so the user knows to fill it manually.
          setPropertyFound(false);
          toast.warning(countyLookupMessage(county), { duration: 7000 });
        }
      } catch {
        setPropertyFound(false);
        toast.warning(countyLookupMessage(county), { duration: 7000 });
      } finally {
        setSearchingProperty(false);
      }
    }
  };

  const handleNextStep = async () => {
    if (wizardStep === 1 && !form.customer_id) {
      toast.error(t('estimates.wizard.customerRequired'));
      return;
    }
    if (wizardStep === 2) {
      // Fix E (2026-08-31) follow-up (testing_agent iteration 197, HIGH):
      // clicking Next during the ~5-10s window while handleVerificationConfirmed's
      // async owner/county lookup is still running (searchingProperty=true) hit
      // the "County is required" error even though verification WAS successful —
      // the county mirror-back just hadn't landed yet. Block with a clear message
      // instead of a confusing false-negative error.
      if (searchingProperty) {
        toast.info(t('estimates.wizard.lookupInProgress', { defaultValue: 'Looking up the property — please wait a moment and try again.' }));
        return;
      }
      let projectId = form.project_id;
      let projectCounty = null;

      // Auto-create project if the new project form is open and has address, title,
      // or a parcel number (Fix E, 2026-08-31 — vacant lots with no street address
      // yet, only a parcel number, must still be able to advance to the next step).
      if (!projectId && showNewProjectForm && (newProjectForm.title.trim() || newProjectForm.address.trim() || newProjectForm.parcel_id.trim())) {
        const created = await handleCreateProject();
        if (!created) return; // creation failed, error already shown
        projectId = created.id;
        projectCounty = created.location?.county || newProjectForm.county;
      }

      if (!projectId) {
        toast.error(t('estimates.wizard.projectRequired', { defaultValue: 'Select or create a project first' }));
        return;
      }

      // Require county before advancing to Well Info
      if (!projectCounty) {
        const projectData = projects.find(p => p.id === projectId);
        projectCounty = projectData?.county || projectData?.location?.county;
      }
      if (!projectCounty) {
        toast.error(t('estimates.wizard.countyRequired', { defaultValue: 'County is required. Please add the county to the project address before continuing.' }));
        return;
      }
    }
    setWizardStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setWizardStep(prev => Math.max(prev - 1, 1));
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit: 'unit', unit_price: 0, sku: '', name: '' }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const moveItem = (idx, dir) => {
    const items = [...form.items];
    const newIdx = idx + (dir === 'up' ? -1 : 1);
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setForm({ ...form, items });
  };
  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(val) || 0 : val };
    setForm({ ...form, items });
  };
  const calcSubtotal = () => form.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0);
  const calcDiscountAmount = () => {
    const subtotal = calcSubtotal();
    if (form.discount_type === 'percentage' && form.discount_value > 0) {
      return Math.round(subtotal * (form.discount_value / 100) * 100) / 100;
    } else if (form.discount_type === 'fixed' && form.discount_value > 0) {
      return Math.min(form.discount_value, subtotal);
    }
    return 0;
  };
  const calcTotal = () => calcSubtotal() - calcDiscountAmount();

  // Search ALL products by SKU (unified search across line_items and inventory)
  const searchLineItems = async (query, idx) => {
    setActiveItemIdx(idx);
    setLineItemSearch(query);
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    if (!query || query.length < 1) {
      setLineItemResults([]);
      return;
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/products/search', { params: { q: query } });
        setLineItemResults(res.data);
      } catch { setLineItemResults([]); }
    }, 150);
  };

  // Select a line item from search results
  const selectLineItem = (lineItem, idx) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      sku: lineItem.sku,
      name: lineItem.name,
      description: lineItem.description || lineItem.name,
      quantity: lineItem.default_quantity || 1,
      unit: lineItem.unit || 'unit',
      unit_price: lineItem.unit_price || 0
    };
    setForm({ ...form, items });
    setLineItemResults([]);
    setActiveItemIdx(null);
    setLineItemSearch('');
    toast.success(t('estimates.itemAdded', { name: lineItem.name }));
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    
    // In wizard mode, only save if we're on step 3 (items) - was step 4
    if (!editing && wizardStep < 3) {
      // Don't save if not on final step
      return;
    }
    
    // Title is now auto-generated by backend if empty
    if (!form.customer_id && !editing) { 
      toast.error(t('estimates.wizard.customerRequired')); 
      return; 
    }
    try {
      let savedEstimate;
      if (editing) {
        const res = await api.put(`/estimates/${editing.id}`, form);
        savedEstimate = res.data;
        toast.success(t('success.updated'));
      } else {
        const res = await api.post('/estimates', form);
        savedEstimate = res.data;
        toast.success(t('success.created'));
      }
      // Save stays on the SAME edit screen — Alian: "yo quiero que al dar
      // Save se quede en la edicion, que no vaya a ningun lado. Si quiero
      // ver el preview, doy Preview." Setting `editing` (instead of closing
      // or jumping to a preview) also turns a brand-new estimate into an
      // editable one with the Preview/Save Changes buttons now available.
      setEditing(savedEstimate);
      fetchData();
      return savedEstimate;
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const openSendDialog = (est) => setSendDialogEst(est);

  const handleSendDialogSent = (updated) => {
    setSendDialogEst(null);
    fetchData();
    // Keep the edit dialog in sync if it's open on this estimate
    if (updated?.id && editing?.id === updated.id) {
      setEditing(prev => ({ ...prev, ...updated }));
    }
  };

  const openAcceptDialog = (id) => {
    setAcceptEstimateId(id);
    setAcceptComment('');
    setAcceptDialogOpen(true);
  };

  const handleAccept = async () => {
    if (!acceptEstimateId) return;
    try {
      await api.post(`/estimates/${acceptEstimateId}/accept`, { comment: acceptComment || '' });
      toast.success('Estimado aceptado');
      setAcceptDialogOpen(false);
      fetchData();
      // Fix B (2026-08-21): accepting from inside the open estimate must NOT
      // close it — it just refreshes editing.status so the Accept button (and
      // the "accepted" step in the stepper) reflect the new state immediately.
      if (editing?.id === acceptEstimateId) {
        setEditing(prev => ({ ...prev, status: 'accepted', acceptance_comment: acceptComment || prev.acceptance_comment }));
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  // Fix B (2026-08-21): clicking a step in the estimate workflow bar. Accepted
  // and Rejected reuse the existing dialogs (comment / mandatory reason) —
  // every other step is a plain status write. Gated by the same permission
  // that hides the Accept button (canAcceptEstimate).
  const handleEstimateStatusStepClick = async (key) => {
    if (!editing || key === editing.status) return;
    if (!canAcceptEstimate) return;
    if (key === 'accepted') { openAcceptDialog(editing.id); return; }
    if (key === 'rejected') { handleReject(editing.id); return; }
    try {
      await api.put(`/estimates/${editing.id}`, { status: key });
      setEditing(prev => ({ ...prev, status: key }));
      fetchData();
      toast.success(t('success.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    }
  };

  const handleConvertToInvoice = async (id) => {
    try {
      const res = await api.post(`/estimates/${id}/convert-to-invoice`);
      const invoice = res.data.invoice;
      toast.success(`Factura ${invoice.invoice_number} creada`);
      if (!res.data.approved) {
        toast.warning('El estimado todavía no fue aprobado por el cliente.');
      }
      fetchData();
      navigate('/invoices', { state: { openInvoiceId: invoice.id } });
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleReject = (id) => {
    setRejectEstimateId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectEstimateId) return;
    if (!rejectReason.trim()) {
      toast.error(t('estimates.rejectReasonRequired'));
      return;
    }
    try {
      await api.post(`/estimates/${rejectEstimateId}/reject`, { reason: rejectReason.trim() });
      toast.success(t('estimates.estimateRejected'));
      setRejectDialogOpen(false);
      fetchData();
      if (editing?.id === rejectEstimateId) {
        setEditing(prev => ({ ...prev, status: 'rejected', rejection_reason: rejectReason.trim() }));
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/estimates/${id}`); toast.success(t('success.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const downloadPDF = async (est) => {
    try {
      const token = localStorage.getItem('bw_token');
      const res = await fetch(`${API_URL}/api/pdf/estimate/${est.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('PDF error');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Estimado_${est.estimate_number || 'N-A'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t('common.download'));
    } catch { toast.error(t('errors.generic')); }
  };

  const openPreview = async (est) => {
    try {
      const token = localStorage.getItem('bw_token');
      setPreviewInlineLoading(true);
      setPreviewInlineOpen(true);
      setPreviewInlineUrl(null);
      const res = await fetch(`${API_URL}/api/pdf/estimate/${est.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('PDF error');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewInlineUrl(url);
    } catch { 
      toast.error(t('errors.generic'));
      setPreviewInlineOpen(false);
    } finally {
      setPreviewInlineLoading(false);
    }
  };

  const getCustomerName = (id) => { const c = contacts.find(c => c.id === id); return c ? contactPrimaryLabel(c) : '-'; };

  // Auto-generate estimate title based on customer info
  const generateEstimateTitle = (customerId, projectId = null) => {
    const customer = contacts.find(c => c.id === customerId);
    if (!customer) return '';
    
    const customerName = contactPrimaryLabel(customer);
    const project = projectId ? projects.find(p => p.id === projectId) : null;
    const date = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    
    if (project) {
      return `${customerName} - ${project.title}`;
    }
    return `${customerName} - ${date}`;
  };

  // Fix A (2026-08-21): el bloque BILL TO salia vacio para unos clientes y con la
  // direccion EQUIVOCADA para otros (Alian: "no entiendo por que hay algunos que si
  // lo sacan y otros no" / "este NO es el mailing address de Darny"). Los contactos
  // guardan la direccion en dos lugares: el array estructurado addresses[] (label
  // "mailing" | "billing", lo crea la importacion de Excel) y los campos planos
  // legacy. Esta funcion leia SOLO los planos. Ahora: 1) busca la entrada con
  // label "mailing"; 2) si no hay, usa los campos planos; 3) NUNCA cae en
  // addresses[0], que es lo que hacia aparecer una direccion de BILLING como si
  // fuera la postal.
  const pickMailingAddress = (contact) => {
    if (!contact) return null;
    const list = Array.isArray(contact.addresses) ? contact.addresses : [];
    const mailing = list.find(a => (a?.label || '').toLowerCase() === 'mailing');
    if (mailing && (mailing.address || mailing.city || mailing.zip)) {
      return {
        address: mailing.address || '',
        city: mailing.city || '',
        state: mailing.state || 'FL',
        zip: mailing.zip || '',
      };
    }
    if (contact.address || contact.city || contact.zip_code || contact.zip) {
      return {
        address: contact.address || '',
        city: contact.city || '',
        state: contact.state || 'FL',
        zip: contact.zip_code || contact.zip || '',
      };
    }
    return null;
  };

  // Handle customer selection with auto-title and auto-populate mailing address
  const handleCustomerSelect = (customerId) => {
    const newTitle = !editing && !form.title ? generateEstimateTitle(customerId, form.project_id) : form.title;
    const selectedCustomer = contacts.find(c => c.id === customerId);
    const mailingAddr = selectedCustomer
      ? (pickMailingAddress(selectedCustomer) || { address: '', city: '', state: 'FL', zip: '' })
      : form.mailing_address;
    setForm({ ...form, customer_id: customerId, title: newTitle, mailing_address: mailingAddr });
  };

  // Handle project selection with auto-fill from data flow
  const handleProjectSelect = async (projectId) => {
    const newTitle = !editing && form.customer_id ? generateEstimateTitle(form.customer_id, projectId) : form.title;
    setForm(prev => ({ ...prev, project_id: projectId, title: newTitle }));
    if (!projectId) return;
    try {
      const res = await api.get(`/data-flow/from-project/${projectId}`);
      const d = res.data;
      setForm(prev => ({
        ...prev,
        project_id: projectId,
        title: newTitle || prev.title,
        customer_id: d.customer_id || prev.customer_id,
        well_address: d.well_address || prev.well_address,
        well_city: d.well_city || prev.well_city,
        well_state: d.well_state || prev.well_state || 'FL',
        well_zip: d.well_zip || prev.well_zip,
        location: d.location || prev.location,
        well_coordinates: d.well_coordinates || prev.well_coordinates,
        property_info: d.property_info || prev.property_info,
      }));
      toast.info('Datos del proyecto heredados');
    } catch { /* ignore */ }
  };

  // TEMA A (Site Inspection): cuando el tipo de trabajo es una inspección de
  // sitio, Alian no conoce todavía profundidad/casing/método/ubicación del
  // pozo (justo va a inspeccionar para averiguarlos) — se oculta y deja de
  // exigirse TODO el bloque técnico, dejando solo cliente + dirección + items.
  const isSiteInspection = form.well_info?.work_type === 'site_inspection';

  return (
    <div data-testid="estimates-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('estimates.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('estimates.totalEstimates', { count: estimates.length })}</p>
        </div>
        {hasPermission('estimates', 'create') && <Button data-testid="new-estimate-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> {t('estimates.newEstimate')}
        </Button>}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-estimates" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('header.searchPlaceholder')} className="pl-10 h-9 bg-white" />
        </div>
        <div className="tabs-scroll">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
            <TabsList className="bg-white border border-gray-200 h-9">
              {STATUSES.map(s => (
                <TabsTrigger key={s} value={s} data-testid={`est-filter-${s}`} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">
                  {t(`estimates.statuses.${s}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : estimates.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('estimates.noEstimates')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('estimates.createFirstEstimate')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">#</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('estimates.estimateTitle')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('common.customer')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">Parcel ID</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.total')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.map((est) => (
                    <tr key={est.id} data-testid={`estimate-row-${est.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => !['converted_to_job', 'converted_to_invoice'].includes(est.status) && openEdit(est)}>
                      <td className="px-5 py-3.5 text-sm font-mono text-gray-500">{est.estimate_number}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{est.title}</p>
                        {est.well_address ? (
                          <p className="text-xs text-blue-600 mt-0.5 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />{est.well_address}
                          </p>
                        ) : est.property_info?.parcel_id && (
                          // Fix E (2026-08-31): vacant lots with no street address —
                          // show the parcel number in the same spot so the estimate
                          // is still identifiable in the list.
                          <p className="text-xs text-indigo-600 mt-0.5 flex items-center gap-1 font-mono">
                            <MapPin className="w-3 h-3 flex-shrink-0" />Parcel {est.property_info.parcel_id}
                          </p>
                        )}
                        {est.property_info?.owner_name && (
                          <p className="text-xs text-gray-400 mt-0.5">Owner: {est.property_info.owner_name}</p>
                        )}
                        {est.converted_to_invoice_id && (
                          <button
                            type="button"
                            data-testid={`estimate-invoice-link-${est.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate('/invoices', { state: { openInvoiceId: est.converted_to_invoice_id } }); }}
                            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                          >
                            <ExternalLink className="w-2.5 h-2.5" />
                            {est.converted_to_invoice_number || 'Invoice'}
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell">{getCustomerName(est.customer_id)}</td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        {est.property_info?.parcel_id ? (
                          <span data-testid={`est-parcel-${est.id}`} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            {est.property_info.parcel_id}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={est.status} /></td>
                      <td className="px-5 py-3.5 text-right text-sm font-semibold text-gray-700">
                        ${(est.total || 0).toLocaleString()}
                        <AttachedFormsBadge
                          linkedType="estimate"
                          linkedId={est.id}
                          count={formCounts[est.id] ?? null}
                          onClick={() => { setCustomFormsEstimate(est); setCustomFormsOpen(true); }}
                          className="ml-2"
                        />
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`estimate-actions-${est.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!['converted_to_invoice', 'converted_to_job'].includes(est.status) && (
                              <DropdownMenuItem data-testid={`send-estimate-${est.id}`} onClick={() => openSendDialog(est)}>
                                <Send className="w-3.5 h-3.5 mr-2" />{est.status === 'draft' ? t('estimates.send') : t('estimates.resend', { defaultValue: 'Resend' })}
                              </DropdownMenuItem>
                            )}
                            {['draft', 'sent', 'viewed'].includes(est.status) && (
                              <DropdownMenuItem data-testid={`reject-estimate-${est.id}`} onClick={() => handleReject(est.id)}><XCircle className="w-3.5 h-3.5 mr-2 text-red-500" />{t('estimates.reject')}</DropdownMenuItem>
                            )}
                            {canAcceptEstimate && ['draft', 'sent'].includes(est.status) && (
                              <DropdownMenuItem data-testid={`accept-estimate-${est.id}`} onClick={() => openAcceptDialog(est.id)}><CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-600" />Accept</DropdownMenuItem>
                            )}
                            {!est.converted_to_invoice_id && hasPermission('invoices', 'create') && (
                              <DropdownMenuItem data-testid={`convert-to-invoice-${est.id}`} onClick={() => handleConvertToInvoice(est.id)}>
                                <FileText className="w-3.5 h-3.5 mr-2 text-blue-600" />Convert to Invoice
                              </DropdownMenuItem>
                            )}
                            {!['converted_to_invoice', 'converted_to_job'].includes(est.status) && <DropdownMenuItem onClick={() => openEdit(est)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => openPreview(est)}><Eye className="w-3.5 h-3.5 mr-2 text-purple-500" />{t('estimates.preview')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadPDF(est)}><FileDown className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('estimates.downloadPdf')}</DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => { setCustomFormsEstimate(est); setCustomFormsOpen(true); }}
                              data-testid={`estimate-custom-forms-${est.id}`}
                            >
                              <ScrollText className="w-3.5 h-3.5 mr-2 text-emerald-600" />Custom Forms
                            </DropdownMenuItem>
                            {est.public_token && (
                              <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/estimate/${est.public_token}`); toast.success(t('estimates.linkCopied')); }}>
                                <LinkIcon className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('estimates.publicLink')}
                              </DropdownMenuItem>
                            )}
                            {est.converted_to_invoice_id && (
                              <DropdownMenuItem data-testid={`view-invoice-${est.id}`} className="text-blue-600" onClick={() => navigate('/invoices', { state: { openInvoiceId: est.converted_to_invoice_id } })}>
                                <FileText className="w-3.5 h-3.5 mr-2" />View Invoice {est.converted_to_invoice_number || ''}
                              </DropdownMenuItem>
                            )}
                            {hasPermission('estimates', 'delete') && <DropdownMenuItem onClick={() => handleDelete(est.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : estimates.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('estimates.noEstimates')}</p>
          </CardContent></Card>
        ) : (
          estimates.map((est, idx) => (
            <Card key={est.id} data-testid={`estimate-mobile-${est.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{est.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <p className="text-xs text-gray-400 font-mono">{est.estimate_number}</p>
                      {est.property_info?.parcel_id && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          {est.property_info.parcel_id}
                        </span>
                      )}
                      <AttachedFormsBadge
                        linkedType="estimate"
                        linkedId={est.id}
                        count={formCounts[est.id] ?? null}
                        onClick={() => { setCustomFormsEstimate(est); setCustomFormsOpen(true); }}
                      />
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!['converted_to_invoice', 'converted_to_job'].includes(est.status) && (
                        <DropdownMenuItem data-testid={`send-estimate-mobile-${est.id}`} onClick={() => openSendDialog(est)}>
                          <Send className="w-3.5 h-3.5 mr-2" />{est.status === 'draft' ? t('estimates.send') : t('estimates.resend', { defaultValue: 'Resend' })}
                        </DropdownMenuItem>
                      )}
                      {['draft', 'sent', 'viewed'].includes(est.status) && (
                        <DropdownMenuItem data-testid={`reject-estimate-mobile-${est.id}`} onClick={() => handleReject(est.id)}><XCircle className="w-3.5 h-3.5 mr-2 text-red-500" />{t('estimates.reject')}</DropdownMenuItem>
                      )}
                      {canAcceptEstimate && ['draft', 'sent'].includes(est.status) && (
                        <DropdownMenuItem data-testid={`accept-estimate-mobile-${est.id}`} onClick={() => openAcceptDialog(est.id)}><CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-600" />Accept</DropdownMenuItem>
                      )}
                      {!est.converted_to_invoice_id && hasPermission('invoices', 'create') && (
                        <DropdownMenuItem onClick={() => handleConvertToInvoice(est.id)}><FileText className="w-3.5 h-3.5 mr-2 text-blue-600" />Convert to Invoice</DropdownMenuItem>
                      )}
                      {!['converted_to_invoice', 'converted_to_job'].includes(est.status) && <DropdownMenuItem onClick={() => openEdit(est)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => openPreview(est)}><Eye className="w-3.5 h-3.5 mr-2 text-purple-500" />{t('estimates.preview')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPDF(est)}><FileDown className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('estimates.downloadPdf')}</DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => { setCustomFormsEstimate(est); setCustomFormsOpen(true); }}
                        data-testid={`estimate-mobile-custom-forms-${est.id}`}
                      >
                        <ScrollText className="w-3.5 h-3.5 mr-2 text-emerald-600" />Custom Forms
                      </DropdownMenuItem>
                      {est.public_token && (
                        <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/estimate/${est.public_token}`); toast.success(t('estimates.linkCopied')); }}>
                          <LinkIcon className="w-3.5 h-3.5 mr-2 text-violet-500" />{t('estimates.publicLink')}
                        </DropdownMenuItem>
                      )}
                      {est.converted_to_invoice_id && (
                        <DropdownMenuItem className="text-blue-600" onClick={() => navigate('/invoices', { state: { openInvoiceId: est.converted_to_invoice_id } })}>
                          <FileText className="w-3.5 h-3.5 mr-2" />View Invoice {est.converted_to_invoice_number || ''}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleDelete(est.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={est.status} />
                  </div>
                  <span className="text-sm font-bold text-gray-800">${(est.total || 0).toLocaleString()}</span>
                </div>
                {est.customer_id && <p className="text-xs text-gray-500 mt-2">{t('common.customer')}: {getCustomerName(est.customer_id)}</p>}
                {est.well_address ? (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" />{est.well_address}
                  </p>
                ) : est.property_info?.parcel_id && (
                  <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1 font-mono">
                    <MapPin className="w-3 h-3 flex-shrink-0" />Parcel {est.property_info.parcel_id}
                  </p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />

      {/* Create/Edit Dialog - Wizard for Create, Full Form for Edit */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open) {
          if (showWellMapVerification) return; // don't close the wizard while the map modal is open/closing
          setDialogOpen(false);
        } else setDialogOpen(open);
      }}>
        <DialogContent
          className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => { if (showWellMapVerification) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (showWellMapVerification) e.preventDefault(); }}
        >
          <>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="font-['Barlow'] text-xl">
                {editing ? t('estimates.editEstimate') : t('estimates.newEstimate')}
              </DialogTitle>
              {editing && !isViewMode && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">Editando</span>
              )}
            </div>
            {editing && <RelatedDocumentsBar entityType="estimate" entityId={editing.id} className="mt-2" />}
            {/* Fix B (2026-08-21): workflow bar arriba del estimado — Alian:
                "necesito un botoncito que me pueda decir... esto yo lo puedo
                ajustar, igual que el de nosotros. Pero me hace falta que me
                salga acá arriba." Statuses/order/color come from Settings >
                Estimate Status (same pattern as Settings > Job Status). */}
            {editing && (
              <div className="mt-3 pb-2 border-b border-gray-100" data-testid="estimate-status-workflow-bar">
                <StatusStepper
                  statuses={estimateStatusFlow}
                  currentKey={editing.status}
                  onSelect={handleEstimateStatusStepClick}
                  canEdit={canAcceptEstimate}
                  sideKeys={['rejected', 'converted_to_invoice']}
                  testidPrefix="estimate-status-step"
                  listTestId="estimate-status-step-list"
                />
              </div>
            )}
            {editing && editing.status === 'rejected' && (
              <div className="mt-2 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2" data-testid="estimate-rejection-reason-banner">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-red-700">
                  <p className="font-semibold">{t('estimates.rejectionReason')}{editing.rejected_by ? ` (${editing.rejected_by})` : ''}</p>
                  <p>{editing.rejection_reason || '—'}</p>
                </div>
              </div>
            )}
          </DialogHeader>
          
          {/* Wizard Steps Indicator - Only show for new estimates */}
          {!editing && (
            <div className="flex items-center justify-between mb-6 px-2">
              {[
                { num: 1, icon: Users, label: t('estimates.wizard.step1') },
                { num: 2, icon: FolderKanban, label: t('estimates.wizard.step2') },
                { num: 3, icon: FileText, label: t('estimates.wizard.step4') },
              ].map((step, idx) => (
                <div key={step.num} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => step.num <= wizardStep && setWizardStep(step.num)}
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
                      wizardStep === step.num 
                        ? 'bg-[#003366] text-white' 
                        : wizardStep > step.num 
                          ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200' 
                          : 'bg-gray-100 text-gray-400'
                    }`}
                    disabled={step.num > wizardStep}
                  >
                    <div className="flex items-center gap-1.5">
                      {wizardStep > step.num ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <step.icon className="w-4 h-4" />
                      )}
                      <span className="text-xs font-semibold hidden sm:inline">{step.label}</span>
                    </div>
                  </button>
                  {idx < 2 && (
                    <ChevronRight className={`w-5 h-5 mx-1 ${wizardStep > step.num ? 'text-emerald-400' : 'text-gray-300'}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className={`space-y-3 ${editing && isViewMode ? 'view-mode-form' : ''}`}>
            {/* STEP 1: Customer Selection */}
            {(!editing && wizardStep === 1) && (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b">
                  <Users className="w-12 h-12 mx-auto text-[#003366] mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('estimates.wizard.selectOrCreateCustomer')}</h3>
                </div>
                
                {!showNewCustomerForm ? (
                  <div className="space-y-4">
                    {/* Property Owner hint (only present when the estimate started from a Maps
                        search) — makes explicit that the county-record owner is a PROPERTY
                        detail, not necessarily the Customer requesting this estimate. */}
                    {form.property_info?.owner_name && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="map-owner-hint-banner">
                        <Landmark className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-semibold">{t('estimates.wizard.propertyOwnerHint', { name: form.property_info.owner_name })}</p>
                          <p className="mt-0.5">{t('estimates.wizard.propertyOwnerHintDesc')}</p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>{t('estimates.customer')}</Label>
                      <ContactCombobox
                        contacts={contacts.filter(c => c.is_customer)}
                        value={form.customer_id}
                        onChange={handleCustomerSelect}
                        placeholder={t('estimates.selectCustomer')}
                        testId="estimate-customer-select"
                      />
                    </div>
                    
                    {form.customer_id && (
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <Check className="w-5 h-5" />
                          <span className="font-medium">{getCustomerName(form.customer_id)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                      <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-500">o</span></div>
                    </div>
                    
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewCustomerForm(true)}
                      className="w-full h-12 border-dashed border-2"
                      data-testid="create-new-customer-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('estimates.wizard.createNewCustomer')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-blue-50/50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900">{t('estimates.wizard.createNewCustomer')}</h4>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewCustomerForm(false)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                    
                    {/* Account Type Selection - FIRST */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold text-gray-700">{t('contacts.accountType')} *</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setNewCustomerForm({...newCustomerForm, account_type: 'residential', company_name: ''})}
                          data-testid="new-customer-residential"
                          className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                            newCustomerForm.account_type === 'residential' 
                              ? 'border-emerald-500 bg-emerald-500 text-white' 
                              : 'border-gray-200 bg-white hover:border-emerald-300'
                          }`}
                        >
                          <User className="w-5 h-5" />
                          <div className="text-left">
                            <span className="text-sm font-medium block">{t('contacts.accountTypes.residential')}</span>
                            <span className={`text-xs ${newCustomerForm.account_type === 'residential' ? 'text-emerald-100' : 'text-gray-400'}`}>
                              {t('contacts.residentialDesc')}
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewCustomerForm({...newCustomerForm, account_type: 'commercial'})}
                          data-testid="new-customer-commercial"
                          className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                            newCustomerForm.account_type === 'commercial' 
                              ? 'border-emerald-500 bg-emerald-500 text-white' 
                              : 'border-gray-200 bg-white hover:border-emerald-300'
                          }`}
                        >
                          <Building className="w-5 h-5" />
                          <div className="text-left">
                            <span className="text-sm font-medium block">{t('contacts.accountTypes.commercial')}</span>
                            <span className={`text-xs ${newCustomerForm.account_type === 'commercial' ? 'text-emerald-100' : 'text-gray-400'}`}>
                              {t('contacts.commercialDesc')}
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                    
                    {/* Conditional Fields */}
                    <div className="space-y-3">
                      {/* Company Name - Only for Commercial */}
                      {newCustomerForm.account_type === 'commercial' && (
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                            <Building className="w-3 h-3" /> {t('contacts.companyName')} *
                          </Label>
                          <Input 
                            placeholder={t('contacts.companyName')} 
                            value={newCustomerForm.company_name}
                            onChange={e => setNewCustomerForm({...newCustomerForm, company_name: toTitleCase(e.target.value)})}
                            data-testid="new-customer-company"
                            className="h-10"
                          />
                        </div>
                      )}
                      
                      {/* Name Fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1">
                            {newCustomerForm.account_type === 'commercial' ? t('contacts.contactName') : t('contacts.firstName')} *
                          </Label>
                          <Input 
                            placeholder={t('contacts.firstName')} 
                            value={newCustomerForm.first_name}
                            onChange={e => setNewCustomerForm({...newCustomerForm, first_name: toTitleCase(e.target.value)})}
                            data-testid="new-customer-first-name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1">{t('contacts.lastName')}</Label>
                          <Input 
                            placeholder={t('contacts.lastName')} 
                            value={newCustomerForm.last_name}
                            onChange={e => setNewCustomerForm({...newCustomerForm, last_name: toTitleCase(e.target.value)})}
                            data-testid="new-customer-last-name"
                          />
                        </div>
                      </div>
                      
                      {/* Contact Info */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {t('common.phone')}
                          </Label>
                          <Input 
                            placeholder="(786) 510-4512" 
                            value={newCustomerForm.phone}
                            onChange={e => setNewCustomerForm({...newCustomerForm, phone: formatPhoneNumber(e.target.value)})}
                            data-testid="new-customer-phone"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {t('common.email')}
                          </Label>
                          <Input 
                            placeholder="correo@ejemplo.com" 
                            type="email"
                            value={newCustomerForm.email}
                            onChange={e => setNewCustomerForm({...newCustomerForm, email: e.target.value})}
                            data-testid="new-customer-email"
                          />
                        </div>
                      </div>
                      
                      {/* Address */}
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> 
                          {newCustomerForm.account_type === 'commercial' ? t('contacts.companyAddress') : t('common.address')}
                        </Label>
                        <AddressAutocomplete
                          value={newCustomerForm.address || ''}
                          onChange={(addressData) => setNewCustomerForm({
                            ...newCustomerForm,
                            address: addressData.address || ''
                          })}
                          placeholder={t('common.address')}
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="button" 
                      onClick={handleCreateCustomer}
                      className="w-full bg-[#003366] hover:bg-[#004080]"
                      data-testid="save-new-customer-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {t('estimates.wizard.createNewCustomer')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Project Selection */}
            {(!editing && wizardStep === 2) && (
              <div className="space-y-4">
                <div className="text-center pb-4 border-b">
                  <FolderKanban className="w-12 h-12 mx-auto text-[#003366] mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('estimates.wizard.selectOrCreateProject')}</h3>
                  <p className="text-sm text-gray-500 mt-1">{t('common.optional')}</p>
                </div>
                
                {!showNewProjectForm ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t('estimates.project')}</Label>
                      <Select value={form.project_id} onValueChange={handleProjectSelect}>
                        <SelectTrigger data-testid="estimate-project-select" className="h-12">
                          <SelectValue placeholder={t('estimates.linkProject')} />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              <div className="flex items-center gap-2">
                                <FolderKanban className="w-4 h-4 text-gray-400" />
                                <span>{p.project_number} - {p.title}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {form.project_id && (
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <Check className="w-5 h-5" />
                          <span className="font-medium">{projects.find(p => p.id === form.project_id)?.title}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                      <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-500">o</span></div>
                    </div>
                    
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleOpenNewProjectForm}
                      className="w-full h-12 border-dashed border-2"
                      data-testid="create-new-project-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('estimates.wizard.createNewProject')}
                    </Button>
                    
                    <p className="text-xs text-center text-gray-400">{t('estimates.wizard.noProjectSelected')}</p>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-blue-50/50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900">{t('estimates.wizard.createNewProject')}</h4>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewProjectForm(false)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                    
                    {/* Auto-generated Project Number Display */}
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                          <FolderKanban className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-emerald-600 font-medium">{t('projects.projectNumber')} <span className="text-emerald-400 font-normal">(unique, auto-generated)</span></p>
                          {loadingProjectNumber ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                              <span className="text-sm text-emerald-700">Generating...</span>
                            </div>
                          ) : (
                            <p className="text-lg font-bold text-emerald-800 font-mono">{autoProjectNumber}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {/* Address FIRST — auto-fills the project title */}
                      <AddressAutocomplete
                        value={newProjectForm.address}
                        onChange={(addressData) => {
                          const addr = addressData.address || '';
                          const isTyping = addressData.lat === null;
                          setNewProjectForm(prev => ({
                            ...prev,
                            address: addr,
                            // While the user is still typing (no dropdown option
                            // picked yet) clear any stale city/county/zip/coords
                            // left over from a PREVIOUS selection, so they can
                            // never be saved against a different address. State is
                            // kept as-is (FL default or whatever was already picked)
                            // instead of being cleared (TEMA G2 — same pattern as
                            // ProjectsPage.jsx::handleAddressChange).
                            city: isTyping ? '' : (addressData.city || prev.city),
                            county: isTyping ? '' : (addressData.county || prev.county),
                            state: isTyping ? prev.state : (stateNameToCode(addressData.state) || prev.state || 'FL'),
                            zip: isTyping ? '' : (addressData.zip_code || prev.zip),
                            lat: isTyping ? null : (addressData.lat ? parseFloat(addressData.lat) : prev.lat),
                            lng: isTyping ? null : (addressData.lng ? parseFloat(addressData.lng) : prev.lng),
                            // Title field is now labeled "Property Owner" — it will be auto-filled
                            // by handleVerificationConfirmed when the legal owner is found from
                            // county records. Do NOT auto-fill with address (Alian's video 1:09-1:58).
                          }));
                        }}
                        placeholder="Job Site Address"
                        data-testid="new-project-address"
                      />
                      <div className="grid grid-cols-3 gap-3">
                        <Input 
                          placeholder={t('common.city')} 
                          value={newProjectForm.city}
                          onChange={e => setNewProjectForm({...newProjectForm, city: toTitleCase(e.target.value)})}
                          data-testid="new-project-city"
                        />
                        <Select
                          value={newProjectForm.state || 'FL'}
                          onValueChange={v => setNewProjectForm({...newProjectForm, state: v, county: v === 'FL' ? newProjectForm.county : ''})}
                        >
                          <SelectTrigger data-testid="new-project-state-select">
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input 
                          placeholder={t('common.zip')} 
                          value={newProjectForm.zip}
                          onChange={async (e) => {
                            const zip = e.target.value;
                            setNewProjectForm(prev => ({...prev, zip}));
                            if (zip.length === 5 && !newProjectForm.county) {
                              try {
                                const res = await api.get(`/geocode/county-by-zip?zip_code=${zip}`);
                                if (res.data?.success && res.data.county) {
                                  setNewProjectForm(prev => ({...prev, county: res.data.county}));
                                }
                              } catch { /* silent */ }
                            }
                          }}
                          data-testid="new-project-zip"
                        />
                      </div>
                      {(newProjectForm.state || 'FL') === 'FL' ? (
                        <Select value={newProjectForm.county} onValueChange={v => setNewProjectForm({...newProjectForm, county: v})}>
                          <SelectTrigger data-testid="new-project-county-select">
                            <SelectValue placeholder={t('common.county')} />
                          </SelectTrigger>
                          <SelectContent>
                            {counties.map(c => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={newProjectForm.county}
                          onChange={e => setNewProjectForm({...newProjectForm, county: e.target.value})}
                          placeholder={t('common.county')}
                          data-testid="new-project-county-input"
                        />
                      )}
                      {/* Parcel Number — Fix E (2026-08-31): Alian's vacant-lot buyers only
                          have this, no street address yet. */}
                      <div>
                        <Label className="text-xs text-gray-500 mb-1">Parcel Number (if the property has no street address)</Label>
                        <Input
                          placeholder="e.g. 12-34-56-789-000"
                          value={newProjectForm.parcel_id}
                          onChange={e => setNewProjectForm({...newProjectForm, parcel_id: e.target.value})}
                          data-testid="new-project-parcel-id"
                          className="font-mono"
                        />
                      </div>
                      {/* Project Title — labeled "Property Owner" per Alian's video request (1:09-1:58).
                          Auto-fills from the legal property owner found on the county map. */}
                      <div>
                        <Label className="text-xs text-gray-500 mb-1">
                          Property Owner
                          <span className="text-gray-400"> — {t('projects.autoFromAddress', { defaultValue: 'auto-filled from county records' })}</span>
                        </Label>
                        <Input 
                          placeholder={newProjectForm.address ? 'Legal property owner name…' : 'Verify the address to auto-fill the owner'} 
                          value={newProjectForm.title}
                          onChange={e => setNewProjectForm({...newProjectForm, title: toTitleCase(e.target.value)})}
                          data-testid="new-project-title"
                          className={!newProjectForm.title && newProjectForm.address ? 'border-emerald-300 bg-emerald-50/30' : ''}
                        />
                        {!newProjectForm.title && !newProjectForm.address && (
                          <p className="text-[10px] text-gray-400 mt-1">
                            The owner name is looked up from county records when the address is verified. You can also type it manually.
                          </p>
                        )}
                      </div>
                      
                      {/* Verify Address / Verify Parcel Button */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleVerifyAddress}
                        // Fix E (2026-08-31): Alian tiene clientes con terrenos baldios que NO tienen
                        // direccion fisica, solo numero de parcela ("lo unico que me puede dar la
                        // persona es el numero de parcela... y no me estas dejando salir hacia
                        // adelante"). Ahora alcanza con UNO de los dos. El backend ya sabe buscar por
                        // parcel_id: property.py _search_parcel_impl(parcel_id=...).
                        disabled={!newProjectForm.address && !newProjectForm.parcel_id}
                        className={`w-full h-12 border-2 transition-all ${
                          addressVerified 
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                            : 'border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50'
                        }`}
                        data-testid="verify-address-btn"
                      >
                        {addressVerified ? (
                          <>
                            <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-600" />
                            <span>{newProjectForm.address ? 'Address Verified' : 'Parcel Verified'}</span>
                          </>
                        ) : (
                          <>
                            <Map className="w-5 h-5 mr-2" />
                            <span>{(newProjectForm.address || !newProjectForm.parcel_id) ? t('estimates.verifyAddressBtn') : t('estimates.verifyParcelBtn', { defaultValue: 'Verify Parcel' })}</span>
                          </>
                        )}
                      </Button>
                      
                      {/* Verification Info Card */}
                      {addressVerified && wellVerificationData && (
                        <div className={`p-3 rounded-lg border ${
                          wellVerificationData.wellData?.contamination_zone?.in_zone 
                            ? 'bg-amber-50 border-amber-300' 
                            : 'bg-emerald-50 border-emerald-300'
                        }`}>
                          <div className="flex items-start gap-2">
                            {wellVerificationData.wellData?.contamination_zone?.in_zone ? (
                              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            ) : (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            )}
                            <div className="text-xs">
                              {wellVerificationData.wellData?.contamination_zone?.in_zone ? (
                                <>
                                  <p className="font-semibold text-amber-800">{wellVerificationData.wellData.contamination_zone.zone_name}</p>
                                  <p className="text-amber-700">Profundidad mínima: {wellVerificationData.wellData.contamination_zone.min_depth_ft} ft</p>
                                </>
                              ) : (
                                <p className="font-medium text-emerald-800">{t('estimates.safeZone')} - No contamination detected</p>
                              )}
                              {wellVerificationData.wellData?.statistics && (
                                <p className="text-gray-600 mt-1">
                                  {wellVerificationData.wellData.statistics.total_wells} nearby wells • 
                                  Recommended depth: {wellVerificationData.wellData.statistics.recommended_depth_ft} ft
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Property Owner / Parcel auto-lookup result (Alian's video min 7:00) */}
                      {addressVerified && (searchingProperty || propertyFound !== null || form.property_info?.owner_name || form.property_info?.parcel_id) && (
                        <div className="p-3 rounded-lg border bg-blue-50 border-blue-300" data-testid="property-owner-result">
                          <div className="flex items-start gap-2">
                            {searchingProperty ? (
                              <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                            ) : (
                              <Landmark className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            )}
                            <div className="text-xs flex-1">
                              <p className="font-semibold text-blue-900 mb-1">
                                {searchingProperty ? 'Looking up legal property owner…' : 'Legal Owner & County Parcel'}
                              </p>
                              {!searchingProperty && (form.property_info?.owner_name || form.property_info?.parcel_id) && (
                                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-blue-800">
                                  {form.property_info?.owner_name && (
                                    <p data-testid="owner-name-display"><span className="text-blue-500">Owner:</span> <span className="font-medium">{form.property_info.owner_name}</span></p>
                                  )}
                                  {form.property_info?.parcel_id && (
                                    <p data-testid="parcel-id-display"><span className="text-blue-500">Parcel:</span> <span className="font-mono">{form.property_info.parcel_id}</span></p>
                                  )}
                                  {form.property_info?.owner_address && (
                                    <p className="col-span-2" data-testid="owner-mailing-address-display">
                                      <span className="text-blue-500">Mailing Address:</span>{' '}
                                      <span className="font-medium">
                                        {form.property_info.owner_address}, {form.property_info.owner_city} {form.property_info.owner_state} {form.property_info.owner_zip}
                                      </span>
                                    </p>
                                  )}
                                  {form.property_info?.county && (
                                    <p><span className="text-blue-500">County:</span> {form.property_info.county}</p>
                                  )}
                                  {form.property_info?.acreage > 0 && (
                                    <p><span className="text-blue-500">Acres:</span> {form.property_info.acreage}</p>
                                  )}
                                </div>
                              )}
                              {!searchingProperty && propertyFound === false && !form.property_info?.owner_name && (
                                <div className="text-blue-600 italic">
                                  <p>No public record found in this county. You can enter the data manually below.</p>
                                  {appraiserLink && (
                                    <a
                                      href={appraiserLink.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      data-testid="estimate-appraiser-link"
                                      className="not-italic text-blue-700 font-medium hover:underline inline-flex items-center gap-1 mt-1"
                                    >
                                      <ExternalLink className="w-3 h-3" /> Open Property Appraiser {appraiserLink.county ? `(${appraiserLink.county})` : ''}
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Property Owner Mailing Address — legal requirement (F): the
                          Permit Application asks for the owner's Legal Name +
                          Mailing Address AND separately the Well Location
                          Address; these can be two different addresses (e.g. an
                          LLC owner living out of state). */}
                      {addressVerified && (
                        <div className="space-y-2 p-3 rounded-lg border border-gray-200 bg-gray-50/50" data-testid="mailing-address-section">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="mailing-same-as-site"
                              checked={mailingSameAsSite}
                              onCheckedChange={(checked) => {
                                setMailingSameAsSite(!!checked);
                                if (checked) {
                                  setForm(prev => ({
                                    ...prev,
                                    property_info: {
                                      ...prev.property_info,
                                      owner_address: newProjectForm.address || '',
                                      owner_city: newProjectForm.city || '',
                                      owner_state: 'FL',
                                      owner_zip: newProjectForm.zip || '',
                                    },
                                  }));
                                }
                              }}
                              data-testid="mailing-same-as-site-checkbox"
                            />
                            <Label htmlFor="mailing-same-as-site" className="text-xs text-gray-700 cursor-pointer">
                              Mailing address is the same as the job site address
                            </Label>
                          </div>
                          {!mailingSameAsSite && (
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Owner mailing address"
                                value={form.property_info?.owner_address || ''}
                                onChange={e => setForm(prev => ({ ...prev, property_info: { ...prev.property_info, owner_address: e.target.value } }))}
                                className="col-span-2 h-9 text-xs"
                                data-testid="owner-mailing-address-input"
                              />
                              <Input
                                placeholder="City"
                                value={form.property_info?.owner_city || ''}
                                onChange={e => setForm(prev => ({ ...prev, property_info: { ...prev.property_info, owner_city: e.target.value } }))}
                                className="h-9 text-xs"
                                data-testid="owner-mailing-city-input"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  placeholder="State"
                                  value={form.property_info?.owner_state || 'FL'}
                                  onChange={e => setForm(prev => ({ ...prev, property_info: { ...prev.property_info, owner_state: e.target.value } }))}
                                  className="h-9 text-xs"
                                  data-testid="owner-mailing-state-input"
                                />
                                <Input
                                  placeholder="Zip"
                                  value={form.property_info?.owner_zip || ''}
                                  onChange={e => setForm(prev => ({ ...prev, property_info: { ...prev.property_info, owner_zip: e.target.value } }))}
                                  className="h-9 text-xs"
                                  data-testid="owner-mailing-zip-input"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button 
                      type="button" 
                      onClick={handleCreateProject}
                      className="w-full bg-[#003366] hover:bg-[#004080]"
                      data-testid="save-new-project-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {t('estimates.wizard.createNewProject')}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* STEP 3: Items (or Full Edit Form) - Previously Step 4 */}
            {(editing || wizardStep === 3) && (
              <div className="space-y-4">
                {!editing && (
                  <div className="text-center pb-4 border-b">
                    <FileText className="w-12 h-12 mx-auto text-[#003366] mb-2" />
                    <h3 className="text-lg font-semibold text-gray-900">{t('estimates.wizard.estimateItems')}</h3>
                  </div>
                )}

                {/* Summary for wizard mode */}
                {!editing && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">{t('estimates.customer')}</p>
                      <p className="font-medium text-sm truncate">{getCustomerName(form.customer_id) || '-'}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">{t('estimates.project')}</p>
                      <p className="font-medium text-sm truncate">{projects.find(p => p.id === form.project_id)?.title || projects.find(p => p.id === form.project_id)?.project_number || '-'}</p>
                    </div>
                  </div>
                )}

                {/* CUSTOMER + PROJECT — compact, at the very top so it's visible without scroll */}
                {editing && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('estimates.customer')}</Label>
                      <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                        <SelectTrigger data-testid="estimate-customer-select" className="h-8 text-sm bg-white">
                          <SelectValue placeholder={t('estimates.selectCustomer')} />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts.filter(c => c.is_customer).map(c => (
                            <SelectItem key={c.id} value={c.id}>{contactPrimaryLabel(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('estimates.project')}</Label>
                      <Select value={form.project_id} onValueChange={handleProjectSelect}>
                        <SelectTrigger data-testid="estimate-project-select" className="h-8 text-sm bg-white">
                          <SelectValue placeholder={t('estimates.linkProject')} />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_number} - {p.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* REGISTER OWNER + PROPERTY ID Header */}
                {(editing && !isViewMode) ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2.5 rounded-xl bg-gradient-to-r from-[#003366]/5 to-blue-50 border border-[#003366]/10" data-testid="estimate-owner-parcel-header">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-[#003366]/60 font-semibold">Register Owner</Label>
                      <Input
                        value={form.property_info?.owner_name || ''}
                        onChange={e => setForm({...form, property_info: {...form.property_info, owner_name: e.target.value}})}
                        placeholder="Property owner name"
                        className="bg-white text-sm h-8 font-semibold text-[#003366]"
                        data-testid="edit-register-owner"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-[#003366]/60 font-semibold">Property ID / Parcel</Label>
                      <Input
                        value={form.property_info?.parcel_id || ''}
                        onChange={e => setForm({...form, property_info: {...form.property_info, parcel_id: e.target.value}})}
                        placeholder="Parcel ID"
                        className="bg-white text-sm h-8 font-mono text-[#003366]"
                        data-testid="edit-property-id"
                      />
                    </div>
                  </div>
                ) : (form.property_info?.owner_name || form.property_info?.parcel_id) && (
                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-gradient-to-r from-[#003366]/5 to-blue-50 border border-[#003366]/10" data-testid="estimate-owner-parcel-header">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-[#003366]/60 font-semibold">Register Owner</p>
                      <p className="text-sm font-bold text-[#003366] truncate" data-testid="estimate-register-owner">
                        {form.property_info?.owner_name || form.title || '-'}
                      </p>
                    </div>
                    {form.property_info?.parcel_id && (
                      <div className="text-right flex-shrink-0">
                        <p className="text-[10px] uppercase tracking-wider text-[#003366]/60 font-semibold">Property ID</p>
                        <p className="text-sm font-bold text-[#003366] font-mono" data-testid="estimate-property-id">
                          {form.property_info.parcel_id}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* DUAL ADDRESS DISPLAY: Mailing Address + Job Site Address — SIEMPRE visible */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="estimate-address-cards">
                    {/* MAILING ADDRESS */}
                    <div className="p-2.5 rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50" data-testid="estimate-mailing-card">
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-500 to-slate-600 flex items-center justify-center flex-shrink-0">
                          <User className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Bill To / Mailing Address</p>
                          {(editing && !isViewMode) ? (
                            <div className="space-y-1.5">
                              <Input
                                value={form.mailing_address?.address || ''}
                                onChange={e => setForm({...form, mailing_address: {...form.mailing_address, address: e.target.value}})}
                                placeholder="Street address"
                                className="bg-white text-sm h-8"
                                data-testid="edit-mailing-address"
                              />
                              <div className="grid grid-cols-3 gap-1.5">
                                <Input
                                  value={form.mailing_address?.city || ''}
                                  onChange={e => setForm({...form, mailing_address: {...form.mailing_address, city: e.target.value}})}
                                  placeholder="City"
                                  className="bg-white text-sm h-8"
                                  data-testid="edit-mailing-city"
                                />
                                <Input
                                  value={form.mailing_address?.state || ''}
                                  onChange={e => setForm({...form, mailing_address: {...form.mailing_address, state: e.target.value}})}
                                  placeholder="State"
                                  className="bg-white text-sm h-8"
                                  data-testid="edit-mailing-state"
                                />
                                <Input
                                  value={form.mailing_address?.zip || ''}
                                  onChange={e => setForm({...form, mailing_address: {...form.mailing_address, zip: e.target.value}})}
                                  placeholder="ZIP"
                                  className="bg-white text-sm h-8"
                                  data-testid="edit-mailing-zip"
                                />
                              </div>
                            </div>
                          ) : form.mailing_address?.address ? (
                            <>
                              <p className="text-sm font-medium text-gray-900" data-testid="estimate-mailing-address">{form.mailing_address.address}</p>
                              {(form.mailing_address.city || form.mailing_address.state || form.mailing_address.zip) && (
                                <p className="text-xs text-gray-600">
                                  {[form.mailing_address.city, form.mailing_address.state].filter(Boolean).join(', ')} {form.mailing_address.zip || ''}
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-gray-400 italic">From customer contact</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* JOB SITE ADDRESS — siempre visible, con Parcel + Project Number agrupados debajo */}
                    <div className="p-2.5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50/80 to-slate-50/80" data-testid="estimate-jobsite-card">
                        <div className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-0.5">Job Address</p>
                            {(editing && !isViewMode) ? (
                              <div className="space-y-1.5">
                                <Input
                                  value={form.well_address || ''}
                                  onChange={e => setForm({...form, well_address: e.target.value})}
                                  placeholder="Enter drilling site address..."
                                  className="bg-white text-sm h-8"
                                  data-testid="edit-jobsite-address"
                                />
                                <div className="grid grid-cols-3 gap-1.5">
                                  <Input
                                    value={form.well_city || ''}
                                    onChange={e => setForm({...form, well_city: e.target.value})}
                                    placeholder="City"
                                    className={`bg-white text-sm h-8 ${form.well_address && !form.well_city ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`}
                                    data-testid="edit-jobsite-city"
                                  />
                                  <Input
                                    value={form.well_state || ''}
                                    onChange={e => setForm({...form, well_state: e.target.value})}
                                    placeholder="State"
                                    className="bg-white text-sm h-8"
                                    data-testid="edit-jobsite-state"
                                  />
                                  <Input
                                    value={form.well_zip || ''}
                                    onChange={e => setForm({...form, well_zip: e.target.value})}
                                    placeholder="ZIP"
                                    className="bg-white text-sm h-8"
                                    data-testid="edit-jobsite-zip"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-1.5">
                                  <Input
                                    value={form.property_info?.county || ''}
                                    onChange={e => setForm({...form, property_info: {...form.property_info, county: e.target.value}})}
                                    placeholder="County"
                                    className="bg-white text-sm h-8"
                                    data-testid="edit-jobsite-county"
                                  />
                                  <Input
                                    value={form.property_info?.parcel_id || ''}
                                    onChange={e => setForm({...form, property_info: {...form.property_info, parcel_id: e.target.value}})}
                                    placeholder="Parcel ID"
                                    className="bg-white text-sm h-8 font-mono"
                                    data-testid="edit-jobsite-parcel"
                                  />
                                </div>
                              </div>
                            ) : form.well_address ? (
                              <>
                                <p className="text-sm font-medium text-gray-900" data-testid="estimate-jobsite-address">{form.well_address}</p>
                                {(form.well_city || form.well_state || form.well_zip) && (
                                  <p className="text-xs text-gray-600" data-testid="estimate-jobsite-city-state-zip">
                                    {[form.well_city, form.well_state].filter(Boolean).join(', ')} {form.well_zip || ''}
                                  </p>
                                )}
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                                  {form.property_info?.county && (
                                    <span className="text-xs text-gray-600" data-testid="estimate-jobsite-county">
                                      {form.property_info.county} County
                                    </span>
                                  )}
                                  {form.property_info?.parcel_id ? (
                                    <span className="text-xs text-blue-700 font-mono font-semibold" data-testid="estimate-jobsite-parcel">
                                      Parcel: {form.property_info.parcel_id}
                                    </span>
                                  ) : (
                                    // TEMA F2: never leave this silently blank — offer a retry
                                    // instead (address typo, county temporarily down, etc.)
                                    <button
                                      type="button"
                                      onClick={retrySearchParcel}
                                      disabled={searchingProperty}
                                      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-medium disabled:opacity-50"
                                      data-testid="estimate-jobsite-parcel-retry-btn"
                                    >
                                      {searchingProperty ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                      {searchingProperty ? t('estimates.property.searching', 'Searching parcel…') : t('estimates.property.retrySearch', 'No Parcel ID found — Retry search')}
                                    </button>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="text-sm font-medium text-gray-900" data-testid="estimate-jobsite-address"><span className="text-gray-400 italic text-xs">No set — select a project</span></p>
                            )}
                            {form.well_address && !form.well_city && !inferCityFromAddress(form.well_address) && (
                              <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1.5 flex items-center gap-1" data-testid="jobsite-city-missing-warning">
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {t('estimates.property.cityMissing', 'City is missing — please fill it in')}
                              </p>
                            )}
                          </div>
                        </div>
                    </div>
                </div>



                {/* WELL TECHNICAL INFORMATION SECTION */}
                <div className={`border rounded-xl overflow-hidden ${editing ? 'border-gray-200 bg-gray-50/50' : 'border-blue-200 bg-gradient-to-br from-blue-50/50 to-slate-50'}`}>
                  <button
                    type="button"
                    onClick={() => setShowWellInfoSection(!showWellInfoSection)}
                    className={`w-full p-2.5 flex items-center justify-between transition-colors ${editing ? 'bg-gradient-to-r from-gray-100 to-slate-100 hover:from-gray-150 hover:to-slate-150' : 'bg-gradient-to-r from-blue-100 to-slate-100 hover:from-blue-200 hover:to-slate-200'}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${editing ? 'bg-gradient-to-br from-gray-400 to-gray-500' : 'bg-gradient-to-br from-blue-500 to-cyan-500'}`}>
                        <Droplets className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-900 text-sm">{t('estimates.wellInfo.title')}</p>
                        <p className="text-xs text-gray-500">{t('estimates.wellInfo.subtitle')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200" data-testid="well-info-internal-badge">
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                        Internal
                      </span>
                      {editing && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded-full" data-testid="well-info-locked-badge">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                          Editable
                        </span>
                      )}
                      <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showWellInfoSection ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  
                  {showWellInfoSection && (
                    <div className="p-3 space-y-3">
                      {/* Editable notice when editing */}
                      {editing && (
                        <div className="flex items-start gap-2.5 p-2.5 bg-blue-50 border border-blue-200 rounded-lg" data-testid="well-info-editable-notice">
                          <Pencil className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-blue-700">
                            {t('estimates.wellTechBanner')}
                          </p>
                        </div>
                      )}
                      
                      {/* WELL INFORMATION — Work Type, Construction Method, Intended Use, Septic Distance: one compact row */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b pb-1.5">
                          <Droplets className="w-4 h-4 text-blue-500" />
                          {t('estimates.wellInfo.wellInformation')}
                        </h4>
                        <div className={`grid grid-cols-2 gap-2 ${isSiteInspection ? '' : 'sm:grid-cols-4'}`}>
                          {/* Work Type */}
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Work Type</Label>
                            <Select
                              value={form.well_info?.work_type || 'new_construction'} 
                              onValueChange={v => setForm({...form, well_info: {...form.well_info, work_type: v, 
                                ...(v !== 'abandonment' ? { abandonment_reason: '' } : {}),
                                ...(v !== 'repair' ? { repair_reason: '' } : {}),
                                ...(v !== 'modification' ? { modification_reason: '' } : {}),
                                ...(v !== 'new_construction' ? { contamination_zone: false } : {}),
                                // Alian's video (2:36-2:40): When Work Type = New Construction, 
                                // Intended Use should auto-fill to "domestic" unless already set
                                ...(v === 'new_construction' && !form.well_info?.well_use ? { well_use: 'domestic' } : {})
                              }})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="work-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[
                                  { value: 'new_construction', label: 'New Construction' },
                                  { value: 'repair', label: 'Repair' },
                                  { value: 'abandonment', label: 'Abandonment' },
                                  { value: 'modification', label: 'Modification' },
                                  // Alian: "cualquier tipo de inspección... por eso quiero que
                                  // sea un site inspection" — sin datos técnicos, solo cliente +
                                  // dirección + items (ver isSiteInspection más abajo).
                                  { value: 'site_inspection', label: 'Site Inspection' },
                                ].sort((a, b) => {
                                  const prefs = dropdownPrefs.work_type || [];
                                  const aIdx = prefs.findIndex(p => p.value === a.value);
                                  const bIdx = prefs.findIndex(p => p.value === b.value);
                                  if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
                                  if (aIdx >= 0) return -1;
                                  if (bIdx >= 0) return 1;
                                  return 0;
                                }).map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                    {(dropdownPrefs.work_type || []).find(p => p.value === opt.value) && (
                                      <span className="ml-1 text-[10px] text-blue-400">&#9733;</span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Alian: Site Inspection no exige Construction Method, Intended Use
                              ni Septic Distance — "me elimina toda esta cosa... de todo, todo,
                              todo" — se ocultan por completo, no solo se deshabilitan. */}
                          {!isSiteInspection && (
                          <>
                          {/* Construction Method - 10 options (moved up next to Work Type per Alian's request) */}
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500">{t('estimates.wellInfo.constructionMethod')}</Label>
                            <Select 
                              value={form.well_info?.construction_method || 'rotary'} 
                              onValueChange={v => setForm({...form, well_info: {...form.well_info, construction_method: v}})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="construction-method-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[
                                  { value: 'rotary', label: 'Rotary' },
                                  { value: 'cable_tool', label: 'Cable Tool' },
                                  { value: 'auger', label: 'Auger' },
                                  { value: 'driven', label: 'Driven' },
                                  { value: 'jetted', label: 'Jetted' },
                                  { value: 'combination', label: 'Combination' },
                                  { value: 'hydraulic_point', label: 'Hydraulic Point' },
                                  { value: 'hand_driven', label: 'Hand Driven' },
                                  { value: 'horizontal_drilling', label: 'Horizontal Drilling' },
                                  { value: 'sonic', label: 'Sonic' },
                                ].sort((a, b) => {
                                  const prefs = dropdownPrefs.construction_method || [];
                                  const aIdx = prefs.findIndex(p => p.value === a.value);
                                  const bIdx = prefs.findIndex(p => p.value === b.value);
                                  if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
                                  if (aIdx >= 0) return -1;
                                  if (bIdx >= 0) return 1;
                                  return 0;
                                }).map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                    {(dropdownPrefs.construction_method || []).find(p => p.value === opt.value) && (
                                      <span className="ml-1 text-[10px] text-blue-400">&#9733;</span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Intended Use (expanded well_use) — full FL DEP list (matches Section 3 of State Permit Application) */}
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500">Intended Use</Label>
                            <Select 
                              value={form.well_info?.well_use || 'domestic'} 
                              onValueChange={v => setForm({...form, well_info: {...form.well_info, well_use: v}})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="well-use-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[
                                  // Section 3 — Florida DEP Statewide Permit Application official list
                                  { value: 'domestic', label: 'Domestic' },
                                  { value: 'agricultural_irrigation', label: 'Agricultural Irrigation' },
                                  { value: 'landscape_irrigation', label: 'Landscape Irrigation' },
                                  { value: 'nursery_irrigation', label: 'Nursery Irrigation' },
                                  { value: 'golf_course_irrigation', label: 'Golf Course Irrigation' },
                                  { value: 'recreation_area_irrigation', label: 'Recreation Area Irrigation' },
                                  { value: 'livestock', label: 'Livestock' },
                                  { value: 'aquaculture', label: 'Aquaculture' },
                                  { value: 'public_water_supply', label: 'Public Water Supply (Limited Use / DOH)' },
                                  { value: 'bottled_water_supply', label: 'Bottled Water Supply' },
                                  { value: 'commercial_industrial', label: 'Commercial / Industrial' },
                                  { value: 'industrial', label: 'Industrial' },
                                  { value: 'commercial_industrial_disposal', label: 'Commercial/Industrial Disposal' },
                                  { value: 'monitoring', label: 'Monitoring' },
                                  { value: 'test', label: 'Test' },
                                  { value: 'site_investigation', label: 'Site Investigation' },
                                  { value: 'earth_coupled_geothermal', label: 'Earth-Coupled Geothermal' },
                                  { value: 'hvac_supply', label: 'HVAC Supply' },
                                  { value: 'hvac_return', label: 'HVAC Return' },
                                  { value: 'aquifer_storage_recovery', label: 'Aquifer Storage and Recovery' },
                                  { value: 'recharge', label: 'Recharge' },
                                  { value: 'class_i_injection', label: 'Class I Injection' },
                                  { value: 'class_v_injection', label: 'Class V Injection' },
                                  { value: 'drainage', label: 'Drainage' },
                                  { value: 'remediation', label: 'Remediation / Recovery' },
                                  { value: 'air_sparging', label: 'Air Sparging' },
                                  { value: 'reclamation', label: 'Reclamation' },
                                  { value: 'other', label: 'Other' },
                                ].sort((a, b) => {
                                  const prefs = dropdownPrefs.well_use || [];
                                  const aIdx = prefs.findIndex(p => p.value === a.value);
                                  const bIdx = prefs.findIndex(p => p.value === b.value);
                                  if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
                                  if (aIdx >= 0) return -1;
                                  if (bIdx >= 0) return 1;
                                  return 0;
                                }).map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                    {(dropdownPrefs.well_use || []).find(p => p.value === opt.value) && (
                                      <span className="ml-1 text-[10px] text-blue-400">&#9733;</span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Septic Distance — now always visible in the same compact row (was duplicated conditionally before) */}
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-500">{t('estimates.wellInfo.septicDistance')}</Label>
                            <div className="relative">
                              <Input 
                                type="number"
                                value={form.well_info?.septic_distance || 75}
                                onChange={e => setForm({...form, well_info: {...form.well_info, septic_distance: parseInt(e.target.value) || 0}})}
                                className="h-8 pr-7 bg-white text-sm"
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">ft</span>
                            </div>
                          </div>
                          </>
                          )}
                        </div>

                        {isSiteInspection && (
                          <div className="flex items-start gap-2.5 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="site-inspection-info-banner">
                            <Info className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-emerald-700">
                              No se requiere información técnica del pozo para un Site Inspection (profundidad, casing, método de perforación o mapa de ubicación). Cargá los ítems y el precio de la inspección más abajo.
                            </p>
                          </div>
                        )}
                        
                        {/* Abandonment Reason - conditional */}
                        {form.well_info?.work_type === 'abandonment' && (
                          <div className="space-y-1 max-w-sm">
                            <Label className="text-xs text-gray-500">Abandonment Reason</Label>
                            <Select
                              value={form.well_info?.abandonment_reason || ''}
                              onValueChange={v => setForm({...form, well_info: {...form.well_info, abandonment_reason: v}})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="abandonment-reason-input">
                                <SelectValue placeholder="Select reason..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Well ran dry / low yield">Well ran dry / low yield</SelectItem>
                                <SelectItem value="Pump failure / trapped pump">Pump failure / trapped pump</SelectItem>
                                <SelectItem value="Contamination / water quality">Contamination / water quality</SelectItem>
                                <SelectItem value="Outside of boundary">Outside of boundary</SelectItem>
                                <SelectItem value="Casing collapse / structural failure">Casing collapse / structural failure</SelectItem>
                                <SelectItem value="Well no longer needed">Well no longer needed</SelectItem>
                                <SelectItem value="Replaced by new well">Replaced by new well</SelectItem>
                                <SelectItem value="Regulatory / compliance requirement">Regulatory / compliance requirement</SelectItem>
                                <SelectItem value="Other">Other (see notes)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Repair Reason - conditional */}
                        {form.well_info?.work_type === 'repair' && (
                          <div className="space-y-1 max-w-sm">
                            <Label className="text-xs text-gray-500">Repair Reason</Label>
                            <Select
                              value={form.well_info?.repair_reason || ''}
                              onValueChange={v => setForm({...form, well_info: {...form.well_info, repair_reason: v}})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="repair-reason-input">
                                <SelectValue placeholder="Select reason..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Low yield / low water production">Low yield / low water production</SelectItem>
                                <SelectItem value="Pump failure">Pump failure</SelectItem>
                                <SelectItem value="Casing damage / corrosion">Casing damage / corrosion</SelectItem>
                                <SelectItem value="Screen clogged / encrusted">Screen clogged / encrusted</SelectItem>
                                <SelectItem value="Water quality / contamination issue">Water quality / contamination issue</SelectItem>
                                <SelectItem value="Sand / sediment intrusion">Sand / sediment intrusion</SelectItem>
                                <SelectItem value="Outside of boundary">Outside of boundary</SelectItem>
                                <SelectItem value="Pressure loss">Pressure loss</SelectItem>
                                <SelectItem value="Other">Other (see notes)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Modification Reason - conditional */}
                        {form.well_info?.work_type === 'modification' && (
                          <div className="space-y-1 max-w-sm">
                            <Label className="text-xs text-gray-500">Modification Reason</Label>
                            <Select
                              value={form.well_info?.modification_reason || ''}
                              onValueChange={v => setForm({...form, well_info: {...form.well_info, modification_reason: v}})}
                            >
                              <SelectTrigger className="h-8 bg-white text-sm" data-testid="modification-reason-input">
                                <SelectValue placeholder="Select reason..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Deepen well / increase depth">Deepen well / increase depth</SelectItem>
                                <SelectItem value="Change casing size or material">Change casing size or material</SelectItem>
                                <SelectItem value="Convert use (domestic to irrigation, etc.)">Convert use (domestic to irrigation, etc.)</SelectItem>
                                <SelectItem value="Add or replace screen">Add or replace screen</SelectItem>
                                <SelectItem value="Grouting / seal improvement">Grouting / seal improvement</SelectItem>
                                <SelectItem value="Outside of boundary">Outside of boundary</SelectItem>
                                <SelectItem value="Upgrade to meet current code">Upgrade to meet current code</SelectItem>
                                <SelectItem value="Other">Other (see notes)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      
                      {/* CONSTRUCTION ESTIMATES — reordered per Alian: Total Well Depth on top,
                          then Casing (diameter+material+Total Casing Depth), then Screen/Completion
                          Interval. "Advanced" toggle removed completely — everything always visible.
                          Paired side-by-side with the Well Location map (2-column, casing left /
                          map right) so the map doesn't hog the full width and steal scroll. */}
                      {!isSiteInspection && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 border-b pb-1.5">
                          <Wrench className="w-4 h-4 text-emerald-500" />
                          {t('estimates.wellInfo.constructionEstimates')}
                        </h4>

                        {/* Total Well Depth — always at the very top */}
                        <div className="max-w-[220px] space-y-1">
                          <Label className="text-xs text-gray-600 font-semibold">Total Well Depth</Label>
                          <div className="relative">
                            <Input 
                              type="text" inputMode="numeric"
                              value={form.well_info?.well_depth_str !== undefined ? form.well_info.well_depth_str : String(form.well_info?.well_depth ?? 40)}
                              onFocus={e => e.target.select()}
                              onChange={e => {
                                const raw = e.target.value.replace(/[^0-9]/g, '');
                                setForm({...form, well_info: {...form.well_info, well_depth_str: raw, well_depth: raw === '' ? 0 : parseInt(raw)}});
                              }}
                              onBlur={() => {
                                const val = parseInt(form.well_info?.well_depth_str ?? form.well_info?.well_depth) || 0;
                                setForm({...form, well_info: {...form.well_info, well_depth_str: String(val), well_depth: val}});
                              }}
                              className="h-9 pr-8 bg-white text-sm font-semibold"
                              data-testid="well-depth-input"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">ft</span>
                          </div>
                        </div>

                        {/* Casing: diameter + material + Casing Depth (single number).
                            Alian (video D): "el casing depth no es from-to, es un solo
                            numero" — the From/To range only applies to the Secondary
                            (telescopic) Casing added below. Primary casing stores the
                            single depth in casing_sections[0].to_depth (from_depth
                            stays 0) so the existing PDF mapping (well.casing_depth =
                            primary_casing.to_depth) keeps working unchanged. */}
                        <div className="space-y-1.5 p-2.5 rounded-lg border border-gray-200 bg-gray-50/60">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                              Casing
                              {(form.well_info?.casing_sections || []).length > 1 && (
                                    <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full">Telescopic Well</span>
                                  )}
                                </Label>
                                {(form.well_info?.casing_sections || []).length < 2 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const sections = [...(form.well_info?.casing_sections || [])];
                                      const last = sections[sections.length - 1] || {};
                                      sections.push({ from_depth: last.to_depth || 0, to_depth: last.to_depth || 0, diameter: '2', material: 'pvc' });
                                      setForm({...form, well_info: {...form.well_info, casing_sections: sections}});
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium transition-colors"
                                    data-testid="add-casing-section-btn"
                                  >
                                    <Plus className="w-3 h-3" /> Secondary Casing
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-[90px_140px_96px_24px] gap-2 text-[10px] font-semibold text-gray-400 uppercase px-1">
                                <span>Diameter</span>
                                <span>Material</span>
                                <span className="text-center">Casing Depth</span>
                                <span></span>
                              </div>
                              <div className="space-y-1.5">
                                <div className="grid grid-cols-[90px_140px_96px_24px] gap-2 items-center bg-white rounded-lg px-1.5 py-1.5 border border-gray-200" data-testid="casing-section-0">
                                  {(() => {
                                    const primary = (form.well_info?.casing_sections || [])[0] || { from_depth: 0, to_depth: 0, diameter: '2', material: 'pvc' };
                                    const updatePrimary = (patch) => {
                                      const sections = [...(form.well_info?.casing_sections || [{ from_depth: 0, to_depth: 0, diameter: '2', material: 'pvc' }])];
                                      sections[0] = { ...primary, ...patch };
                                      setForm({...form, well_info: {...form.well_info, casing_sections: sections}});
                                    };
                                    return (
                                      <>
                                        <Select value={primary.diameter || '2'} onValueChange={v => updatePrimary({ diameter: v })}>
                                          <SelectTrigger className="h-8 bg-white text-xs" data-testid="casing-section-0-diameter">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {['2','4','6','8','10','12','16'].map(d => (
                                              <SelectItem key={d} value={d}>{d}"</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Select value={primary.material || 'pvc'} onValueChange={v => updatePrimary({ material: v })}>
                                          <SelectTrigger className="h-8 bg-white text-xs" data-testid="casing-section-0-material">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="pvc">PVC</SelectItem>
                                            <SelectItem value="black_steel">Black Steel</SelectItem>
                                            <SelectItem value="galvanized">Galvanized</SelectItem>
                                            <SelectItem value="stainless_steel">S. Steel</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <Input
                                          type="text" inputMode="numeric"
                                          value={primary.to_depth_str !== undefined ? primary.to_depth_str : String(primary.to_depth ?? 0)}
                                          onFocus={e => e.target.select()}
                                          onChange={e => {
                                            const raw = e.target.value.replace(/[^0-9]/g, '');
                                            updatePrimary({ to_depth_str: raw, to_depth: raw === '' ? 0 : parseInt(raw), from_depth: 0 });
                                          }}
                                          onBlur={() => {
                                            const val = parseInt(primary.to_depth_str ?? primary.to_depth) || 0;
                                            updatePrimary({ to_depth_str: String(val), to_depth: val, from_depth: 0 });
                                          }}
                                          className="h-8 text-center bg-white text-sm font-medium px-1"
                                          placeholder="0"
                                          data-testid="casing-section-0-depth"
                                        />
                                        <span />
                                      </>
                                    );
                                  })()}
                                </div>
                                {(form.well_info?.casing_sections || []).length > 1 && (
                                  <div className="grid grid-cols-[90px_140px_96px_96px_24px] gap-2 items-center bg-white rounded-lg px-1.5 py-1.5 border border-blue-200" data-testid="casing-section-1">
                                    {(() => {
                                      const secondary = form.well_info.casing_sections[1];
                                      const updateSecondary = (patch) => {
                                        const sections = [...form.well_info.casing_sections];
                                        sections[1] = { ...secondary, ...patch };
                                        setForm({...form, well_info: {...form.well_info, casing_sections: sections}});
                                      };
                                      return (
                                        <>
                                          <Select value={secondary.diameter || '2'} onValueChange={v => updateSecondary({ diameter: v })}>
                                            <SelectTrigger className="h-8 bg-white text-xs" data-testid="casing-section-1-diameter">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {['2','4','6','8','10','12','16'].map(d => (
                                                <SelectItem key={d} value={d}>{d}"</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          <Select value={secondary.material || 'pvc'} onValueChange={v => updateSecondary({ material: v })}>
                                            <SelectTrigger className="h-8 bg-white text-xs" data-testid="casing-section-1-material">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="pvc">PVC</SelectItem>
                                              <SelectItem value="black_steel">Black Steel</SelectItem>
                                              <SelectItem value="galvanized">Galvanized</SelectItem>
                                              <SelectItem value="stainless_steel">S. Steel</SelectItem>
                                            </SelectContent>
                                          </Select>
                                          <Input
                                            type="text" inputMode="numeric"
                                            value={secondary.from_depth_str !== undefined ? secondary.from_depth_str : String(secondary.from_depth ?? 0)}
                                            onFocus={e => e.target.select()}
                                            onChange={e => {
                                              const raw = e.target.value.replace(/[^0-9]/g, '');
                                              updateSecondary({ from_depth_str: raw, from_depth: raw === '' ? 0 : parseInt(raw) });
                                            }}
                                            onBlur={() => {
                                              const val = parseInt(secondary.from_depth_str ?? secondary.from_depth) || 0;
                                              updateSecondary({ from_depth_str: String(val), from_depth: val });
                                            }}
                                            className="h-8 text-center bg-white text-sm font-medium px-1"
                                            placeholder="From"
                                            data-testid="casing-section-1-from"
                                          />
                                          <Input
                                            type="text" inputMode="numeric"
                                            value={secondary.to_depth_str !== undefined ? secondary.to_depth_str : String(secondary.to_depth ?? 0)}
                                            onFocus={e => e.target.select()}
                                            onChange={e => {
                                              const raw = e.target.value.replace(/[^0-9]/g, '');
                                              updateSecondary({ to_depth_str: raw, to_depth: raw === '' ? 0 : parseInt(raw) });
                                            }}
                                            onBlur={() => {
                                              const val = parseInt(secondary.to_depth_str ?? secondary.to_depth) || 0;
                                              updateSecondary({ to_depth_str: String(val), to_depth: val });
                                            }}
                                            className="h-8 text-center bg-white text-sm font-medium px-1"
                                            placeholder="To"
                                            data-testid="casing-section-1-to"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const sections = [form.well_info.casing_sections[0]];
                                              setForm({...form, well_info: {...form.well_info, casing_sections: sections}});
                                            }}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                            data-testid="casing-section-1-remove"
                                          >
                                            <XCircle className="w-3.5 h-3.5" />
                                          </button>
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                        </div>

                        {/* Screen / Completion Interval — Alian: "esto nunca va a cambiar, va a ser
                            uno solo, o va a ser con screen o open hole" — single interval, no
                            "Add interval" button (never multiplies). */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-gray-500">{t('estimates.completionIntervals')}</Label>
                            </div>
                            <div className="space-y-2">
                              {(form.well_info?.completion_intervals?.slice(0, 1) || [{ type: 'screen', from_depth: 80, to_depth: 100 }]).map((interval, idx) => (
                                <div key={idx} className="flex items-center gap-1.5 bg-gray-50 rounded-lg p-2 border border-gray-200" data-testid={`completion-interval-${idx}`}>
                                  {/* Screen / Open Hole toggle per interval */}
                                  <div className="flex gap-1 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const intervals = [...(form.well_info?.completion_intervals || [])];
                                        intervals[idx] = { ...intervals[idx], type: 'screen' };
                                        setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                      }}
                                      className={`px-2.5 h-7 rounded text-xs font-medium border transition-all ${
                                        interval.type === 'screen'
                                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                                      }`}
                                      data-testid={`interval-${idx}-screen-btn`}
                                    >
                                      Screen
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const intervals = [...(form.well_info?.completion_intervals || [])];
                                        intervals[idx] = { ...intervals[idx], type: 'open_hole' };
                                        setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                      }}
                                      className={`px-2.5 h-7 rounded text-xs font-medium border transition-all ${
                                        interval.type === 'open_hole'
                                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                                      }`}
                                      data-testid={`interval-${idx}-openhole-btn`}
                                    >
                                      Open Hole
                                    </button>
                                  </div>
                                  {/* From / To depth inputs */}
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    value={interval.from_depth_str !== undefined ? interval.from_depth_str : String(interval.from_depth ?? '')}
                                    onFocus={e => e.target.select()}
                                    onChange={e => {
                                      const raw = e.target.value.replace(/[^0-9]/g, '');
                                      const intervals = [...(form.well_info?.completion_intervals || [])];
                                      intervals[idx] = { ...intervals[idx], from_depth_str: raw, from_depth: raw === '' ? 0 : parseInt(raw) };
                                      setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                    }}
                                    onBlur={e => {
                                      const intervals = [...(form.well_info?.completion_intervals || [])];
                                      const val = parseInt(intervals[idx].from_depth_str) || 0;
                                      intervals[idx] = { ...intervals[idx], from_depth_str: String(val), from_depth: val };
                                      setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                    }}
                                    className="h-8 w-24 flex-shrink-0 text-center bg-white text-sm font-medium px-1"
                                    placeholder="0"
                                    data-testid={`interval-${idx}-from`}
                                  />
                                  <span className="text-gray-400 text-xs flex-shrink-0">—</span>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    value={interval.to_depth_str !== undefined ? interval.to_depth_str : String(interval.to_depth ?? '')}
                                    onFocus={e => e.target.select()}
                                    onChange={e => {
                                      const raw = e.target.value.replace(/[^0-9]/g, '');
                                      const intervals = [...(form.well_info?.completion_intervals || [])];
                                      intervals[idx] = { ...intervals[idx], to_depth_str: raw, to_depth: raw === '' ? 0 : parseInt(raw) };
                                      setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                    }}
                                    onBlur={e => {
                                      const intervals = [...(form.well_info?.completion_intervals || [])];
                                      const val = parseInt(intervals[idx].to_depth_str) || 0;
                                      intervals[idx] = { ...intervals[idx], to_depth_str: String(val), to_depth: val };
                                      setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                    }}
                                    className="h-8 w-24 flex-shrink-0 text-center bg-white text-sm font-medium px-1"
                                    placeholder="0"
                                    data-testid={`interval-${idx}-to`}
                                  />
                                  <span className="text-xs text-gray-400 flex-shrink-0">ft</span>
                                  {/* Remove button — solo si hay más de 1 intervalo */}
                                  {(form.well_info?.completion_intervals || []).length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const intervals = [...(form.well_info?.completion_intervals || [])];
                                        intervals.splice(idx, 1);
                                        setForm({...form, well_info: {...form.well_info, completion_intervals: intervals}});
                                      }}
                                      className="ml-auto text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                      data-testid={`interval-${idx}-remove-btn`}
                                    >
                                      <XCircle className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      
                      {/* WELL LOCATION MARKER MAP — right column, compact, next to Casing */}
                      {(form.well_coordinates?.lat && form.well_coordinates?.lng) && (
                        <div className="border rounded-xl overflow-hidden border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-slate-50">
                          <WellLocationPicker
                            coordinates={form.well_coordinates}
                            wellMarker={form.well_info?.well_marker || null}
                            address={form.well_address}
                            parcelId={form.property_info?.parcel_id || ''}
                            county={form.property_info?.county || ''}
                            savedBoundary={form.property_info?.boundary || []}
                            onBoundaryLoaded={(rings) => setForm(prev => ({...prev, property_info: {...prev.property_info, boundary: rings}}))}
                            compact
                            onSave={(marker) => {
                              setForm({...form, well_info: {...form.well_info, well_marker: marker}});
                              toast.success('Well location saved');
                            }}
                          />
                        </div>
                      )}
                      </div>
                      )}

                      {/* Auto-fill from verification data */}
                      {!isSiteInspection && wellVerificationData?.wellData?.statistics && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-700 flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            {t('estimates.wellInfo.recommendedFromVerification', { depth: wellVerificationData.wellData.statistics.recommended_depth_ft })}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Import from Templates - Prominent Button */}
                {!showTemplateSelector ? (
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border-2 border-dashed border-emerald-300">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-emerald-800">{t('estimates.importFromTemplate')}</p>
                          <p className="text-xs text-emerald-600">{t('estimates.importFromTemplateHint')}</p>
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        onClick={() => {
                          setShowTemplateSelector(true);
                          fetchTemplates();
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="import-from-template-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('estimates.selectTemplate')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {t('estimates.selectTemplateTitle')}
                      </h4>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowTemplateSelector(false)}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                    
                    {loadingTemplates ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                        <span className="ml-2 text-sm text-emerald-700">{t('common.loading')}</span>
                      </div>
                    ) : estimateTemplates.length === 0 ? (
                      <div className="text-center py-6">
                        <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500">{t('estimates.noTemplatesAvailable')}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {estimateTemplates.map(template => (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => applyTemplate(template)}
                            className="p-3 text-left rounded-lg border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400 transition-all group"
                            data-testid={`template-option-${template.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-gray-900 group-hover:text-emerald-700">{template.name}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {template.items?.length || 0} {t('estimates.items').toLowerCase()}
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-600" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Line Items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-gray-700">{t('estimates.items')}</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                      <Plus className="w-3 h-3 mr-1" /> {t('estimates.addItem')}
                    </Button>
                  </div>

                  {/* Inventory auto-suggest removed (Alian's video 2026-02-12: panel was misleading) */}

                  {/* Info about SKU search */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50/50 border border-blue-100">
                    <Tag className="w-4 h-4 text-blue-500" />
                    <p className="text-xs text-blue-600">{t('estimates.skuSearchHint')}</p>
                  </div>
                  
                  <div className="space-y-2.5">
                    {form.items.map((item, idx) => (
                      <div key={idx} className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 space-y-1.5">
                        {/* ONE compact line: move/drag + SKU + Name + Qty + Unit + Price + Subtotal + Delete */}
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {/* Drag handle + up/down buttons */}
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <GripVertical className="w-4 h-4 text-gray-300 cursor-grab active:cursor-grabbing" title="Arrastrar" />
                            <button
                              type="button"
                              onClick={() => moveItem(idx, 'up')}
                              disabled={idx === 0}
                              className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                              title="Mover arriba"
                              data-testid={`item-move-up-${idx}`}
                            >
                              <ChevronUp className="w-3 h-3 text-gray-500" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveItem(idx, 'down')}
                              disabled={idx === form.items.length - 1}
                              className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                              title="Mover abajo"
                              data-testid={`item-move-down-${idx}`}
                            >
                              <ChevronDown className="w-3 h-3 text-gray-500" />
                            </button>
                          </div>

                          <div className="relative w-24 flex-shrink-0">
                            <Input
                              data-testid={`item-sku-${idx}`}
                              value={item.sku || ''}
                              onChange={e => {
                                updateItem(idx, 'sku', e.target.value);
                                searchLineItems(e.target.value, idx);
                              }}
                              onFocus={() => {
                                setActiveItemIdx(idx);
                                if (item.sku) searchLineItems(item.sku, idx);
                              }}
                              placeholder={t('estimates.searchSku')}
                              className="bg-white h-8 font-mono text-xs"
                            />
                            {/* SKU Search Results Dropdown */}
                            {activeItemIdx === idx && lineItemResults.length > 0 && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-auto w-80">
                                {lineItemResults.map((li) => (
                                  <button
                                    key={li.id}
                                    type="button"
                                    onClick={() => selectLineItem(li, idx)}
                                    className="w-full px-3 py-2.5 text-left hover:bg-blue-50 border-b last:border-0 transition-colors"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm text-[#003366] font-bold">{li.sku}</span>
                                        {li.supplier_sku && <span className="text-[10px] text-gray-400 font-mono">({li.supplier_sku})</span>}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-emerald-600">${li.unit_price?.toFixed(2)}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{li.source === 'inventory' ? 'Stock' : 'Artículo'}</span>
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-800 truncate mt-0.5">{li.name}</p>
                                    {li.description && li.description !== li.name && (
                                      <p className="text-xs text-gray-400 truncate">{li.description}</p>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="relative flex-1 min-w-[140px]">
                            <Input
                              data-testid={`item-name-${idx}`}
                              value={item.name || ''}
                              onChange={e => {
                                updateItem(idx, 'name', e.target.value);
                                searchLineItems(e.target.value, idx);
                              }}
                              onFocus={() => {
                                setActiveItemIdx(idx);
                                if (item.name && !item.sku) searchLineItems(item.name, idx);
                              }}
                              placeholder={t('estimates.itemName')}
                              className="flex-1 bg-white h-8 font-medium text-sm w-full"
                            />
                            {/* Name Search Results Dropdown */}
                            {activeItemIdx === idx && lineItemResults.length > 0 && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-auto w-80">
                                {lineItemResults.map((li) => (
                                  <button
                                    key={li.id}
                                    type="button"
                                    onClick={() => selectLineItem(li, idx)}
                                    className="w-full px-3 py-2.5 text-left hover:bg-blue-50 border-b last:border-0 transition-colors"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="font-mono text-sm text-[#003366] font-bold">{li.sku}</span>
                                        {li.supplier_sku && <span className="text-[10px] text-gray-400 font-mono">({li.supplier_sku})</span>}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-emerald-600">${li.unit_price?.toFixed(2)}</span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{li.source === 'inventory' ? 'Stock' : 'Artículo'}</span>
                                      </div>
                                    </div>
                                    <p className="text-sm text-gray-800 truncate mt-0.5">{li.name}</p>
                                    {li.description && li.description !== li.name && (
                                      <p className="text-xs text-gray-400 truncate">{li.description}</p>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <Input
                            data-testid={`item-qty-${idx}`}
                            type="number"
                            value={item.quantity || ''}
                            onChange={e => updateItem(idx, 'quantity', e.target.value)}
                            onFocus={e => e.target.select()}
                            className="w-20 bg-white h-8 text-sm flex-shrink-0"
                            min="0"
                          />
                          <Input
                            data-testid={`item-unit-${idx}`}
                            value={item.unit || 'unit'}
                            onChange={e => updateItem(idx, 'unit', e.target.value)}
                            placeholder="unit"
                            className="w-20 bg-white h-8 text-xs flex-shrink-0"
                          />
                          <span className="text-gray-400 text-xs flex-shrink-0">×</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-gray-500 text-xs">$</span>
                            <Input
                              data-testid={`item-price-${idx}`}
                              type="number"
                              value={item.unit_price || ''}
                              onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                              onFocus={e => e.target.select()}
                              placeholder="Precio"
                              className="w-28 bg-white h-8 text-sm"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <span className="text-gray-400 text-xs flex-shrink-0">=</span>
                          <span className="text-sm font-semibold text-gray-700 min-w-[70px] text-right flex-shrink-0">
                            ${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                          </span>
                          {form.items.length > 1 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="h-8 w-8 text-red-400 hover:text-red-600 flex-shrink-0">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Second line: Description — shown once, auto-expanding textarea */}
                        <AutoGrowTextarea
                          data-testid={`item-desc-${idx}`}
                          value={item.description}
                          onChange={e => updateItem(idx, 'description', e.target.value)}
                          placeholder={t('estimates.itemDescription')}
                          className="bg-white text-xs"
                        />
                      </div>
                    ))}
                    {/* Floating Add item button - stays visible while scrolling the items list (wizard + Edit) */}
                    <div className="sticky bottom-16 z-20 flex justify-center pt-1 pointer-events-none">
                      <Button
                        type="button"
                        size="sm"
                        onClick={addItem}
                        data-testid="add-item-btn-floating"
                        className="pointer-events-auto bg-[#003366] hover:bg-[#004080] text-white shadow-lg rounded-full px-5 h-9"
                      >
                        <Plus className="w-4 h-4 mr-1.5" /> {t('estimates.addItem')}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end px-3">
                    <div className="text-right space-y-1.5">
                      <div className="flex items-center justify-end gap-3">
                        <span className="text-sm text-gray-500">Subtotal:</span>
                        <span className="text-sm font-semibold text-gray-700 min-w-[90px] text-right">${calcSubtotal().toFixed(2)}</span>
                      </div>
                      
                      {/* Discount Section */}
                      <div data-testid="discount-section" className="flex items-center gap-2 py-2 px-3 rounded-lg bg-amber-50/80 border border-amber-200/60">
                        <Tag className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v, discount_value: v === 'none' ? 0 : form.discount_value})}>
                          <SelectTrigger data-testid="discount-type-select" className="h-8 w-[130px] text-xs bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin descuento</SelectItem>
                            <SelectItem value="percentage">% Porcentaje</SelectItem>
                            <SelectItem value="fixed">$ Discount</SelectItem>
                          </SelectContent>
                        </Select>
                        {form.discount_type !== 'none' && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-amber-700">{form.discount_type === 'percentage' ? '%' : '$'}</span>
                            <Input
                              data-testid="discount-value-input"
                              type="number"
                              value={form.discount_value}
                              onChange={e => setForm({...form, discount_value: parseFloat(e.target.value) || 0})}
                              className="w-20 h-8 text-sm bg-white"
                              min="0"
                              max={form.discount_type === 'percentage' ? 100 : undefined}
                              step={form.discount_type === 'percentage' ? 1 : 0.01}
                            />
                          </div>
                        )}
                        {calcDiscountAmount() > 0 && (
                          <span className="text-xs font-medium text-red-600 ml-auto">Discount: -${calcDiscountAmount().toFixed(2)}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-end gap-3 pt-1 border-t border-gray-200">
                        <span className="text-sm font-semibold text-gray-700">Total:</span>
                        <span data-testid="estimate-total" className="text-lg font-bold text-gray-900">${calcTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {editing && (
                  <div className="space-y-2">
                    <Label>{t('common.notes')}</Label>
                    <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder={t('common.notes') + '...'} rows={2} />
                  </div>
                )}

                {/* Contract Checkbox - with link to edit contract */}
                <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="include_contract"
                      checked={form.include_contract}
                      onCheckedChange={v => setForm({ ...form, include_contract: v })}
                    />
                    <div className="flex-1">
                      <Label htmlFor="include_contract" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                        <ScrollText className="w-4 h-4 text-amber-600" />
                        {t('estimates.includeContract')}
                      </Label>
                      <p className="text-xs text-gray-500 mt-0.5">{t('estimates.contractNote')}</p>
                    </div>
                  </div>
                  
                  {/* Link to view/edit contract in Settings */}
                  <div className="mt-3 pt-3 border-t border-amber-200/50 flex items-center justify-between">
                    <p className="text-xs text-amber-700 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      {t('estimates.contractLocation')}
                    </p>
                    <a 
                      href="/settings" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('/settings?tab=contract', '_blank');
                      }}
                    >
                      {t('estimates.viewEditContract')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Documents & Files (incl. auto-archived SIGNED estimate PDF) */}
            {editing && <DocumentsSection entityType="estimate" entityId={editing.id} />}

            {/* Activity Log - Edit Dialog */}
            {editing && <ActivityLog entityType="estimates" entityId={editing.id} />}

            {/* ── STICKY ACTION BAR ─────────────────────────────────── */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 -mx-6 px-6 py-3 mt-4 flex flex-wrap items-center gap-2 z-10" data-testid="estimate-sticky-bar">
              {/* View Mode: Edit, Preview, Send */}
              {editing && isViewMode && (
                <>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="mr-auto text-xs h-8">{t('common.close')}</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => openPreview(editing)} className="border-purple-200 text-purple-700 hover:bg-purple-50 h-8" data-testid="preview-estimate-btn">
                    <Eye className="w-3.5 h-3.5 mr-1" /> {t('estimates.preview')}
                  </Button>
                  {!['converted_to_invoice', 'converted_to_job'].includes(editing.status) && (
                    <Button type="button" size="sm" onClick={() => openSendDialog(editing)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid="send-estimate-btn-sticky">
                      <Send className="w-3.5 h-3.5 mr-1" /> {editing.status === 'draft' ? t('estimates.sendToCustomer') : t('estimates.resend', { defaultValue: 'Resend' })}
                    </Button>
                  )}
                  {/* Convert to Invoice — Alian: available straight from the open
                      estimate, no need to close it and hunt it down in the list.
                      Client approval is NOT required, only a heads-up if missing. */}
                  {editing.converted_to_invoice_id ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => navigate('/invoices', { state: { openInvoiceId: editing.converted_to_invoice_id } })} className="border-blue-200 text-blue-700 hover:bg-blue-50 h-8" data-testid="view-converted-invoice-btn-sticky">
                      <FileText className="w-3.5 h-3.5 mr-1" /> View Invoice {editing.converted_to_invoice_number || ''}
                    </Button>
                  ) : hasPermission('invoices', 'create') && (
                    <Button type="button" size="sm" onClick={() => handleConvertToInvoice(editing.id)} className="bg-blue-600 hover:bg-blue-700 text-white h-8" data-testid="convert-to-invoice-btn-sticky">
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Convert to Invoice
                      {editing.status !== 'accepted' && <span className="ml-1 text-[10px] opacity-80">(not approved yet)</span>}
                    </Button>
                  )}
                  <Button type="button" size="sm" onClick={() => setIsViewMode(false)} className="bg-[#003366] hover:bg-[#004080] text-white h-8" data-testid="edit-mode-btn-sticky">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> {t('common.edit')}
                  </Button>
                  {/* Fix B (2026-08-21): Accept from inside the open estimate —
                      Alian: "si estoy trabajando en él, no tengo cómo aceptarlo".
                      Same openAcceptDialog/canAcceptEstimate already used by the
                      list's 3-dot menu; hidden once accepted/rejected. */}
                  {canAcceptEstimate && editing?.status !== 'accepted' && editing?.status !== 'rejected' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="accept-estimate-from-modal"
                      onClick={() => openAcceptDialog(editing.id)}
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accept
                    </Button>
                  )}
                  {['draft', 'sent', 'viewed'].includes(editing.status) && (
                    <Button type="button" variant="outline" size="sm" onClick={() => handleReject(editing.id)} className="border-red-200 text-red-600 hover:bg-red-50 h-8" data-testid="reject-estimate-btn-sticky">
                      <XCircle className="w-3.5 h-3.5 mr-1" /> {t('estimates.reject')}
                    </Button>
                  )}
                </>
              )}
              {/* Edit Mode: Cancel, Prev/Next/Save */}
              {(!isViewMode || !editing) && (
                <>
                  {!editing && wizardStep > 1 && (
                    <Button type="button" variant="outline" size="sm" onClick={handlePrevStep} className="mr-auto h-8">
                      <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {t('estimates.wizard.prevStep')}
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => { if (!isViewMode && editing) { setIsViewMode(true); } else { setDialogOpen(false); } }} className="h-8">{!isViewMode && editing ? t('common.cancel') : t('common.close')}</Button>
                  {editing && (
                    <Button type="button" variant="outline" size="sm" onClick={() => openPreview(editing)} className="border-purple-200 text-purple-700 hover:bg-purple-50 h-8" data-testid="preview-estimate-btn">
                      <Eye className="w-3.5 h-3.5 mr-1" /> {t('estimates.preview')}
                    </Button>
                  )}
                  {/* Send by email straight from the open estimate (Alian: "no
                      veo nada que diga send... me haría falta que pongas aquí Send") */}
                  {editing && !['converted_to_invoice', 'converted_to_job'].includes(editing.status) && (
                    <Button type="button" variant="outline" size="sm" onClick={async () => { const saved = await handleSave(); openSendDialog(saved || editing); }} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8" data-testid="send-estimate-btn-edit">
                      <Send className="w-3.5 h-3.5 mr-1" /> {editing.status === 'draft' ? t('estimates.sendToCustomer') : t('estimates.resend', { defaultValue: 'Resend' })}
                    </Button>
                  )}
                  {editing && (editing.converted_to_invoice_id ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => navigate('/invoices', { state: { openInvoiceId: editing.converted_to_invoice_id } })} className="border-blue-200 text-blue-700 hover:bg-blue-50 h-8" data-testid="view-converted-invoice-btn-edit">
                      <FileText className="w-3.5 h-3.5 mr-1" /> View Invoice {editing.converted_to_invoice_number || ''}
                    </Button>
                  ) : hasPermission('invoices', 'create') && (
                    <Button type="button" variant="outline" size="sm" onClick={() => handleConvertToInvoice(editing.id)} className="border-blue-200 text-blue-700 hover:bg-blue-50 h-8" data-testid="convert-to-invoice-btn-edit">
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Convert to Invoice
                    </Button>
                  ))}
                  {!editing && wizardStep < 3 ? (
                    <Button type="button" size="sm" onClick={handleNextStep} disabled={wizardStep === 2 && searchingProperty} className="bg-[#003366] hover:bg-[#004080] text-white h-8" data-testid="wizard-next-btn">
                      {wizardStep === 2 && searchingProperty ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Looking up property…</>
                      ) : (
                        <>{t('estimates.wizard.nextStep')} <ChevronRight className="w-3.5 h-3.5 ml-1" /></>
                      )}
                    </Button>
                  ) : (
                    <Button data-testid="save-estimate-btn" type="button" size="sm" onClick={handleSave} className="bg-[#003366] hover:bg-[#004080] text-white h-8">
                      {editing ? t('estimates.saveChanges') : t('estimates.wizard.finishEstimate')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          </>

          {/* Well Map Verification Overlay — rendered as a plain absolutely-positioned
              layer INSIDE the wizard's own DialogContent (not a second Radix <Dialog>).
              Two independent top-level Radix Dialogs each register their own
              DismissableLayer/FocusScope; closing the inner one races with the outer
              one's outside-interaction detection and was closing the whole wizard
              (Alian's video bug). Keeping this as a plain div avoids that entirely. */}
          {showWellMapVerification && (
            <div className="absolute inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" data-testid="well-map-verification-overlay">
                <WellMapVerification
                  address={newProjectForm.address}
                  coordinates={projectCoordinates}
                  onVerified={handleVerificationConfirmed}
                  onClose={() => setShowWellMapVerification(false)}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Accept Estimate Dialog */}
      <Dialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Aceptar Estimado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Confirme la aceptacion del estimado. Agregue un comentario con los detalles de como fue aceptado (llamada telefonica, en persona, etc.)
            </p>
            <div className="space-y-2">
              <Label>Comentario de aceptacion (opcional)</Label>
              <Textarea
                data-testid="accept-comment"
                value={acceptComment}
                onChange={e => setAcceptComment(e.target.value)}
                placeholder="Ej: Cliente acepto por telefono el 24/03/2026. Confirmo terminos y condiciones."
                rows={3}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setAcceptDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="confirm-accept-estimate"
                type="button"
                onClick={handleAccept}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmar Aceptacion
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Estimate Dialog — TEMA A4, requires a reason */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" /> {t('estimates.rejectEstimate')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('estimates.rejectReason')} <span className="text-red-500">*</span></Label>
              <Textarea
                data-testid="reject-reason-input"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder={t('estimates.rejectReasonPlaceholder')}
                rows={3}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setRejectDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                data-testid="confirm-reject-estimate"
                type="button"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                <XCircle className="w-4 h-4 mr-2" /> {t('estimates.confirmReject')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── SEND / RESEND (Email + SMS) ──────────────────────────── */}
      <SendDocumentDialog
        open={!!sendDialogEst}
        onClose={() => setSendDialogEst(null)}
        docType="estimate"
        doc={sendDialogEst}
        customer={contacts.find(c => c.id === sendDialogEst?.customer_id)}
        onSent={handleSendDialogSent}
      />

      {/* ── INLINE PDF PREVIEW MODAL ────────────────────────────── */}
      <Dialog
        open={previewInlineOpen}
        onOpenChange={v => {
          if (!v) {
            setPreviewInlineOpen(false);
            if (previewInlineUrl) { URL.revokeObjectURL(previewInlineUrl); setPreviewInlineUrl(null); }
          }
        }}
      >
        <DialogContent className="max-w-5xl w-full max-h-[95vh] h-[95vh] overflow-hidden flex flex-col p-0">
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
            <DialogTitle className="font-['Barlow'] text-lg flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" /> {t('estimates.preview')}
            </DialogTitle>
          </div>
          <div className="flex-1 overflow-hidden bg-gray-100">
            {previewInlineLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-[#003366] mx-auto mb-3" />
                  <p className="text-sm text-gray-600">{t('estimates.loadingPreview')}</p>
                </div>
              </div>
            ) : previewInlineUrl ? (
              <iframe
                src={previewInlineUrl}
                className="w-full h-full"
                title="Estimate PDF Preview"
                data-testid="preview-iframe"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Eye className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>{t('estimates.previewNotAvailable')}</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Forms Attachment Dialog — Alian video min 47:30: "lo estoy haciendo desde el estimado" */}
      {customFormsEstimate && (
        <AttachCustomForm
          open={customFormsOpen}
          onClose={() => { setCustomFormsOpen(false); setCustomFormsEstimate(null); }}
          linkedType="estimate"
          linkedId={customFormsEstimate.id}
          context={{
            estimate_id: customFormsEstimate.id,
            customer_id: customFormsEstimate.customer_id,
            project_id: customFormsEstimate.project_id,
          }}
          county={
            customFormsEstimate.property_info?.county ||
            customFormsEstimate.well_info?.county ||
            (typeof customFormsEstimate.county === 'string' ? customFormsEstimate.county : null)
          }
          title={`Custom Forms — ${customFormsEstimate.estimate_number || ''}`}
        />
      )}
    </div>
  );
}
